import { useState } from 'react';
import * as React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  PlusCircle,
  Users,
  BookOpen,
  Calendar,
  Mail,
  Phone,
  Edit,
  Trash2,
  Archive,
  ArchiveRestore,
  Search,
  TrendingUp,
  DollarSign,
  Download,
  PenLine,
  CheckCircle2,
  Circle,
  Camera,
  UserRound,
  ChevronDown,
  ChevronUp,
  FileText,
  Ticket,
  Bell,
} from 'lucide-react';
import {
  CANCELLATION_POLICY_OPTIONS,
  DEFAULT_CANCELLATION_POLICY,
} from '@shared/cancellation-policy';
import {
  NO_REGISTRATION_CUTOFF,
  REGISTRATION_CUTOFF_OPTIONS,
  registrationCutoffFromFormValue,
  registrationCutoffToFormValue,
} from '@shared/registration-cutoff';
import {
  DELIVERY_MODES,
  DELIVERY_MODE_LABELS,
  requiresLocalityForDeliveryMode,
  type DeliveryMode,
} from '@shared/delivery-mode';
import { PRICING_MODES, PRICING_MODE_LABELS, type PricingMode } from '@shared/pricing-mode';
import type {
  CreatorEarningsSummaryDto,
  CreatorLedgerEntryDto,
  CreatorPayoutDto,
  CreatorConnectStatusDto,
} from '@shared/api-contracts';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { authedFetch, authedJson } from '@/lib/http';
import { fetchAllPhysicalLocalities, type LocalityOption } from '@/lib/geography';
import { Button } from '@/components/ui/button';
import { SharedPassGate } from '@/components/shared-pass-gate';
import { PassHoldersManager } from '@/components/pass-holders-manager';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PublicShell } from '@/components/public-shell';
import { WriterPortal } from '@/components/writer-portal';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import { normalizeImageUrl, imageErrorFallback, PLACEHOLDER } from '@/lib/image';
import {
  formatDateWithTimezone,
  formatDateCompact,
  formatTimeRange,
  formatDateTimeRange,
  formatSessionCompact,
} from '@/lib/dateUtils';

type RecurrenceFreq = 'weekly' | 'biweekly' | 'monthly';
const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
const WEEKDAY_LABELS: Record<string, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
};

function buildRRule(freq: RecurrenceFreq, days: string[]): string {
  if (freq === 'monthly') return 'FREQ=MONTHLY';
  const byDay = days.length > 0 ? `;BYDAY=${days.join(',')}` : '';
  if (freq === 'biweekly') return `FREQ=WEEKLY;INTERVAL=2${byDay}`;
  return `FREQ=WEEKLY${byDay}`;
}

interface ClassFormState {
  title: string;
  description: string;
  price: string;
  categoryId: string;
  duration: string;
  cancellationPolicy: string;
  registrationCutoff: string;
  registrationCutoffOnlyIfEmpty: boolean;
  deliveryMode: DeliveryMode;
  localityId: string;
  pricingMode: PricingMode;
  suggestedPrice: string;
  promoEnabled: boolean;
  promoPrice: string;
  promoStartsAt: string;
  promoEndsAt: string;
  // Recurrence
  isRecurring: boolean;
  recurrenceFreq: RecurrenceFreq;
  recurrenceDays: string[];
  recurrenceStartTime: string;
  recurrenceEndsAt: string;
  // Pass
  passEnabled: boolean;
  passSessionCount: string;
  passPrice: string;
  passExpiryDays: string;
  // Venue
  venueId: string;
  livestreamClassId: string;
  feedbackEmailEnabled: boolean;
  requiresWaiver: boolean;
  waiverTitle: string;
  waiverText: string;
  waiverVersion: string;
}

interface ClassPayload {
  title: string;
  description: string;
  price: number;
  categoryId: string;
  duration: number;
  cancellationPolicy: string;
  registrationCutoffHours: number | null;
  registrationCutoffOnlyIfEmpty: boolean;
  deliveryMode: DeliveryMode;
  localityId: string | null;
  pricingMode: PricingMode;
  suggestedPrice: number | null;
  promoEnabled: boolean;
  promoPrice: number | null;
  promoStartsAt: string | null;
  promoEndsAt: string | null;
  recurrenceRule: string | null;
  recurrenceEndsAt: string | null;
  recurrenceStartTime: string | null;
  passEnabled: boolean;
  passSessionCount: number | null;
  passPrice: number | null;
  passExpiryDays: number | null;
  venueId: string | null;
  livestreamClassId: string | null;
  feedbackEmailEnabled: boolean;
  requiresWaiver: boolean;
  waiverTitle: string | null;
  waiverText: string | null;
  waiverVersion: number;
}

interface ClassMutationResponse {
  message: string;
  class: { id: string; thumbnailUrl?: string | null };
  imageUrl?: string;
}

function classRequiresPayoutOnboarding(classItem: {
  pricingMode?: unknown;
  price?: unknown;
}): boolean {
  if (classItem.pricingMode === 'free') {
    return false;
  }

  if (typeof classItem.pricingMode === 'string') {
    return true;
  }

  return Number(classItem.price) > 0;
}

function getEmptyClassForm(): ClassFormState {
  return {
    title: '',
    description: '',
    price: '',
    categoryId: '',
    duration: '',
    cancellationPolicy: DEFAULT_CANCELLATION_POLICY,
    registrationCutoff: NO_REGISTRATION_CUTOFF,
    registrationCutoffOnlyIfEmpty: false,
    deliveryMode: 'in_person',
    localityId: '',
    pricingMode: 'standard',
    suggestedPrice: '',
    promoEnabled: false,
    promoPrice: '',
    promoStartsAt: '',
    promoEndsAt: '',
    isRecurring: false,
    recurrenceFreq: 'weekly',
    recurrenceDays: [],
    recurrenceStartTime: '',
    recurrenceEndsAt: '',
    passEnabled: false,
    passSessionCount: '',
    passPrice: '',
    passExpiryDays: '',
    venueId: '',
    livestreamClassId: '',
    feedbackEmailEnabled: false,
    requiresWaiver: false,
    waiverTitle: 'Liability Waiver',
    waiverText: '',
    waiverVersion: '1',
  };
}

function getSessionRemainingSpots(session: {
  maxStudents?: number | null;
  availableSpots?: number | string | null;
  enrolledCount?: number | string | null;
}): number | null {
  if (session.maxStudents == null) return null;
  const available = Number(session.availableSpots);
  if (Number.isFinite(available)) {
    return Math.max(available, 0);
  }
  const enrolled = Number(session.enrolledCount) || 0;
  return Math.max(session.maxStudents - enrolled, 0);
}

function isSessionFull(session: {
  maxStudents?: number | null;
  availableSpots?: number | string | null;
  enrolledCount?: number | string | null;
}): boolean {
  const remaining = getSessionRemainingSpots(session);
  return remaining !== null && remaining <= 0;
}

// Helper function to get default session times (8:00 AM - 9:00 AM next day)
function getDefaultSessionTimes() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const startTime = new Date(tomorrow);
  startTime.setHours(8, 0, 0, 0); // 8:00 AM

  const endTime = new Date(tomorrow);
  endTime.setHours(9, 0, 0, 0); // 9:00 AM

  return {
    startDateTime: utcToLocalDatetimeString(startTime.toISOString()),
    endDateTime: utcToLocalDatetimeString(endTime.toISOString()),
  };
}

// Helper function to convert UTC datetime to local datetime string for datetime-local input
function utcToLocalDatetimeString(utcDateString: string): string {
  const date = new Date(utcDateString);
  // Get the local date components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export default function CreatorDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editingClass, setEditingClass] = useState<any>(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
  });
  const [managingSessionsFor, setManagingSessionsFor] = useState<any>(null);
  const [newSession, setNewSession] = useState(() => ({
    ...getDefaultSessionTimes(),
    maxStudents: '',
    notes: '',
    disablePostClassEmail: false,
    isRecurring: false,
    recurrenceFreq: 'weekly' as RecurrenceFreq,
    recurrenceDays: [] as string[],
    recurrenceEndsAt: '',
  }));
  const [editingSession, setEditingSession] = useState<any>(null);
  const [expandedClassSessions, setExpandedClassSessions] = useState<Set<string>>(new Set());
  const [expandedClassDescriptions, setExpandedClassDescriptions] = useState<Set<string>>(
    new Set()
  );
  const [selectedSessionFilter, setSelectedSessionFilter] = useState<string>('all');
  const [studentSearchTerm, setStudentSearchTerm] = useState<string>('');
  const [studentStatusFilter, setStudentStatusFilter] = useState<string>('all');
  const [viewingStudentHistory, setViewingStudentHistory] = useState<any>(null);
  const [viewingStudentWaivers, setViewingStudentWaivers] = useState<any>(null);
  const [selectedWaiverClassId, setSelectedWaiverClassId] = useState<string>('');
  const [isAddingManualStudent, setIsAddingManualStudent] = useState(false);
  const [manualStudentForm, setManualStudentForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    notes: '',
  });
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const [viewingRosterFor, setViewingRosterFor] = useState<{
    sessionId: string;
    classId: string;
    sessionLabel: string;
  } | null>(null);
  const [unpublishConfirm, setUnpublishConfirm] = useState<{
    classId: string;
    title: string;
    anchorSessionId: string;
    sessionCount: number;
  } | null>(null);
  const [waiverRecipientEmails, setWaiverRecipientEmails] = useState<Record<string, string>>({});
  const [denyingRefundId, setDenyingRefundId] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');
  const [cancelingTicketOrderId, setCancelingTicketOrderId] = useState<string | null>(null);
  const [cancelingSession, setCancelingSession] = useState<{
    id: string;
    startDateTime: string;
  } | null>(null);
  const [cancelScope, setCancelScope] = useState<'single' | 'forward'>('single');
  const [cancelNote, setCancelNote] = useState('');
  const [cancelFeeBearer, setCancelFeeBearer] = useState<'creator' | 'customer'>('creator');
  const [newClass, setNewClass] = useState<ClassFormState>(getEmptyClassForm);
  const [classThumbnailFile, setClassThumbnailFile] = useState<File | null>(null);
  const [classThumbnailPreview, setClassThumbnailPreview] = useState<string>('');
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    bio: '',
    creatorHeadline: '',
    experience: '',
    portfolioUrl: '',
    socialLinks: {
      instagram: '',
      twitter: '',
    },
  });

  const formatWebsiteUrl = (value: string) => {
    if (!value) return '';

    // If the value doesn't start with http:// or https://, add https://
    if (value && !value.match(/^https?:\/\//)) {
      return `https://${value}`;
    }
    return value;
  };

  const handlePortfolioBlur = () => {
    if (profileForm.portfolioUrl) {
      const formattedUrl = formatWebsiteUrl(profileForm.portfolioUrl);
      setProfileForm(prev => ({ ...prev, portfolioUrl: formattedUrl }));
    }
  };

  const replaceClassThumbnailPreview = React.useCallback((nextPreview: string) => {
    setClassThumbnailPreview(currentPreview => {
      if (currentPreview.startsWith('blob:')) {
        URL.revokeObjectURL(currentPreview);
      }
      return nextPreview;
    });
  }, []);

  const resetClassEditor = React.useCallback(() => {
    setEditingClass(null);
    setNewClass(getEmptyClassForm());
    setClassThumbnailFile(null);
    replaceClassThumbnailPreview('');
  }, [replaceClassThumbnailPreview]);

  const handleClassThumbnailChange = (file: File | null) => {
    if (!file) {
      setClassThumbnailFile(null);
      replaceClassThumbnailPreview(editingClass?.thumbnailUrl || '');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File',
        description: 'Please choose an image file.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Image Too Large',
        description: 'Class thumbnails must be 5MB or smaller.',
        variant: 'destructive',
      });
      return;
    }

    setClassThumbnailFile(file);
    replaceClassThumbnailPreview(URL.createObjectURL(file));
  };

  const uploadClassThumbnail = async (
    classId: string,
    file: File
  ): Promise<ClassMutationResponse> => {
    return await authedJson<ClassMutationResponse>(`/api/classes/${classId}/thumbnail`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });
  };

  React.useEffect(() => {
    return () => {
      if (classThumbnailPreview.startsWith('blob:')) {
        URL.revokeObjectURL(classThumbnailPreview);
      }
    };
  }, [classThumbnailPreview]);

  // Handle Stripe Connect return
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectParam = params.get('connect');
    if (connectParam === 'success') {
      toast({
        title: 'Payout setup complete',
        description:
          'Your Stripe Connect account is connected. Earnings will be transferred after the holding period.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/connect/status'] });
      // Clean the URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (connectParam === 'refresh') {
      toast({
        title: 'Stripe onboarding session expired',
        description: 'Please click "Set up payouts" to restart your onboarding.',
        variant: 'destructive',
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  // Fetch creator profile
  const { data: creator, isLoading: creatorProfileLoading } = useQuery({
    queryKey: ['/api/creator/profile'],
    enabled: !!user && user.role === 'creator',
  });

  // Update form when creator data changes
  React.useEffect(() => {
    if (creator) {
      const creatorData = creator as any;
      setProfileImageFailed(false);
      setProfileForm({
        displayName: creatorData?.displayName || '',
        bio: creatorData?.bio || '',
        creatorHeadline: creatorData?.creatorHeadline || '',
        experience: creatorData?.experience || '',
        portfolioUrl: creatorData?.portfolioUrl || '',
        socialLinks: {
          instagram: creatorData?.socialLinks?.instagram || '',
          twitter: creatorData?.socialLinks?.twitter || '',
        },
      });
    }
  }, [creator]);

  const handleProfilePictureUpload = async (file: File | null) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please choose an image file.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Image too large',
        description: 'Profile photos must be 10MB or smaller.',
        variant: 'destructive',
      });
      return;
    }

    setUploadingProfilePicture(true);
    try {
      const response = await authedFetch('/api/upload/profile-picture', {
        method: 'POST',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed');
      }

      const { imageUrl } = (await response.json()) as { imageUrl: string };
      setProfileImageFailed(false);
      queryClient.setQueryData(['/api/creator/profile'], (old: any) =>
        old ? { ...old, profileImageUrl: imageUrl } : old
      );
      queryClient.setQueryData(['/api/auth/user'], (old: any) =>
        old ? { ...old, profileImageUrl: imageUrl } : old
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/creator/profile'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/creators'] }),
      ]);

      toast({
        title: 'Profile photo updated',
        description: 'Your public creator photo has been saved.',
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to upload profile photo. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  // Fetch creator's classes
  const { data: classes, isLoading: classesLoading } = useQuery({
    queryKey: ['/api/creator/classes'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
  });

  // Fetch class enrollments for creator
  const { data: enrollments } = useQuery({
    queryKey: ['/api/creator/enrollments'],
    enabled: !!user && user.role === 'creator',
  });

  const { data: manualStudents = [] } = useQuery<any[]>({
    queryKey: ['/api/creator/students/manual'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
    queryFn: () => authedJson<any[]>('/api/creator/students/manual'),
  });

  const { data: availableWaivers = [] } = useQuery<any[]>({
    queryKey: ['/api/creator/available-waivers'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
    queryFn: () => authedJson<any[]>('/api/creator/available-waivers'),
  });

  const { data: studentWaivers = [], isLoading: studentWaiversLoading } = useQuery<any[]>({
    queryKey: ['/api/creator/students/waivers', viewingStudentWaivers?.studentEmail],
    enabled: !!viewingStudentWaivers?.studentEmail,
    queryFn: () =>
      authedJson<any[]>(
        `/api/creator/students/waivers?email=${encodeURIComponent(viewingStudentWaivers.studentEmail)}`
      ),
  });

  // Fetch creator's schedule
  const { data: schedule, isLoading: scheduleLoading } = useQuery({
    queryKey: ['/api/creator/schedule'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
  });

  // Fetch creator's archived sessions
  const { data: archivedSessions, isLoading: archiveLoading } = useQuery({
    queryKey: ['/api/creator/archive'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
  });

  // Fetch session attendee roster (creator/admin)
  const { data: rosterData, isLoading: rosterLoading } = useQuery({
    queryKey: [
      `/api/classes/${viewingRosterFor?.classId}/sessions/${viewingRosterFor?.sessionId}/attendees`,
    ],
    enabled: !!viewingRosterFor,
    queryFn: async () => {
      const data = await authedJson(
        `/api/classes/${viewingRosterFor!.classId}/sessions/${viewingRosterFor!.sessionId}/attendees`
      );
      return data;
    },
  });

  const waiverReminderMutation = useMutation({
    mutationFn: async (input: {
      classId: string;
      sessionId: string;
      attendeeId: string;
      recipientEmail?: string;
    }) => {
      const response = await apiRequest(
        'POST',
        `/api/classes/${input.classId}/sessions/${input.sessionId}/attendees/${input.attendeeId}/waiver/remind`,
        input.recipientEmail ? { recipientEmail: input.recipientEmail } : undefined
      );
      return response.json();
    },
    onSuccess: (_data, input) => {
      toast({
        title: 'Waiver sent',
        description: 'A fresh signing link was emailed to the attendee.',
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/classes/${input.classId}/sessions/${input.sessionId}/attendees`],
      });
    },
    onError: () => {
      toast({
        title: 'Reminder failed',
        description: 'Could not send a waiver reminder. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const saveManualStudentMutation = useMutation({
    mutationFn: async (data: typeof manualStudentForm) => {
      const response = await apiRequest('POST', '/api/creator/students/manual', data);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Student saved', description: 'The student was added to your list.' });
      setIsAddingManualStudent(false);
      setManualStudentForm({ name: '', email: '', phoneNumber: '', notes: '' });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/students/manual'] });
    },
    onError: () => {
      toast({
        title: 'Could not save student',
        description: 'Check the name and email, then try again.',
        variant: 'destructive',
      });
    },
  });

  const sendStudentWaiverMutation = useMutation({
    mutationFn: async (input: {
      classId: string;
      recipientName: string;
      recipientEmail: string;
      recipientPhone?: string | null;
    }) => {
      const response = await apiRequest('POST', '/api/creator/students/waivers/send', input);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Waiver sent', description: 'The waiver request email was sent.' });
      setSelectedWaiverClassId('');
      queryClient.invalidateQueries({ queryKey: ['/api/creator/students/manual'] });
      if (viewingStudentWaivers?.studentEmail) {
        queryClient.invalidateQueries({
          queryKey: ['/api/creator/students/waivers', viewingStudentWaivers.studentEmail],
        });
      }
    },
    onError: () => {
      toast({
        title: 'Could not send waiver',
        description: 'Choose a waiver and confirm the student email.',
        variant: 'destructive',
      });
    },
  });

  // Fetch creator refund requests
  const { data: refundRequests, isLoading: refundRequestsLoading } = useQuery({
    queryKey: ['/api/refunds/requests/creator'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
    queryFn: async () => {
      const data = await authedJson('/api/refunds/requests/creator');
      return data as any[];
    },
  });

  // Fetch earnings summary
  const { data: earningsSummary, isLoading: earningsSummaryLoading } =
    useQuery<CreatorEarningsSummaryDto>({
      queryKey: ['/api/creator/earnings/summary'],
      enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
      queryFn: async () => {
        return authedJson<CreatorEarningsSummaryDto>('/api/creator/earnings/summary');
      },
    });

  // Fetch earnings ledger
  const { data: earningsLedger, isLoading: earningsLedgerLoading } = useQuery<
    CreatorLedgerEntryDto[]
  >({
    queryKey: ['/api/creator/earnings/ledger'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
    queryFn: async () => {
      return authedJson<CreatorLedgerEntryDto[]>('/api/creator/earnings/ledger?limit=25');
    },
  });

  // Fetch Stripe Connect status
  const { data: connectStatus, isLoading: connectStatusLoading } =
    useQuery<CreatorConnectStatusDto>({
      queryKey: ['/api/creator/connect/status'],
      enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
      queryFn: async () => {
        return authedJson<CreatorConnectStatusDto>('/api/creator/connect/status');
      },
    });

  // Mutation to start Stripe Connect onboarding
  const connectOnboardMutation = useMutation({
    mutationFn: async () => {
      const response = await authedFetch('/api/creator/connect/onboard', {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || 'Failed to start Stripe onboarding.'
        );
      }

      const data = (await response.json()) as { url: string };
      return data;
    },
    onSuccess: data => {
      window.location.href = data.url;
    },
    onError: error => {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to start Stripe onboarding. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const connectDashboardMutation = useMutation({
    mutationFn: async () => {
      const response = await authedFetch('/api/creator/connect/dashboard', {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to open Stripe settings.');
      }

      return (await response.json()) as { url: string };
    },
    onSuccess: data => {
      window.location.href = data.url;
    },
    onError: error => {
      toast({
        title: 'Stripe settings unavailable',
        description:
          error instanceof Error
            ? error.message
            : 'Unable to open Stripe settings. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Fetch payout history
  const { data: payoutHistory, isLoading: payoutHistoryLoading } = useQuery<CreatorPayoutDto[]>({
    queryKey: ['/api/creator/earnings/payouts'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
    queryFn: async () => {
      return authedJson<CreatorPayoutDto[]>('/api/creator/earnings/payouts?limit=10');
    },
  });

  // Fetch per-class and per-session earnings totals for Classes tab display
  const { data: classTotals = [] } = useQuery<{ classId: string | null; earnedCents: number }[]>({
    queryKey: ['/api/creator/earnings/class-totals'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
    queryFn: async () =>
      authedJson<{ classId: string | null; earnedCents: number }[]>(
        '/api/creator/earnings/class-totals'
      ),
  });

  const { data: sessionTotals = [] } = useQuery<
    { sessionId: string | null; earnedCents: number }[]
  >({
    queryKey: ['/api/creator/earnings/session-totals'],
    enabled: !!user && (user.role === 'creator' || user.role === 'admin'),
    queryFn: async () =>
      authedJson<{ sessionId: string | null; earnedCents: number }[]>(
        '/api/creator/earnings/session-totals'
      ),
  });

  const classTotalsMap = Object.fromEntries(
    classTotals.filter(r => r.classId).map(r => [r.classId!, r.earnedCents])
  );
  const sessionTotalsMap = Object.fromEntries(
    sessionTotals.filter(r => r.sessionId).map(r => [r.sessionId!, r.earnedCents])
  );

  // Fetch categories for dropdown
  const { data: categories } = useQuery<any[]>({
    queryKey: ['/api/categories'],
  });

  const { data: localities = [] } = useQuery<LocalityOption[]>({
    queryKey: ['all-physical-localities'],
    queryFn: fetchAllPhysicalLocalities,
    staleTime: 1000 * 60 * 10,
  });

  const { data: venues = [] } = useQuery<
    Array<{ id: string; name: string; address: string | null }>
  >({
    queryKey: ['/api/classes/venues', newClass.localityId],
    queryFn: () =>
      authedJson<Array<{ id: string; name: string; address: string | null }>>(
        `/api/classes/venues?localityId=${newClass.localityId}`
      ),
    enabled: Boolean(newClass.localityId),
    staleTime: 1000 * 60 * 5,
  });

  // ICS token for creator calendar feed
  const creatorId = (creator as any)?.id as string | undefined;
  const { data: icsTokenData, refetch: refetchIcsToken } = useQuery<{ icsToken: string }>({
    queryKey: ['/api/calendar/feed/creators/token', creatorId],
    queryFn: () =>
      authedJson<{ icsToken: string }>(`/api/calendar/feed/creators/${creatorId}/token`),
    enabled: !!creatorId,
    staleTime: Infinity,
  });
  const rotateIcsTokenMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/calendar/feed/creators/${creatorId}/rotate-token`);
      return res.json();
    },
    onSuccess: () => {
      void refetchIcsToken();
      toast({ title: 'Calendar link rotated', description: 'Your previous link is now invalid.' });
    },
    onError: () => toast({ title: 'Failed to rotate link', variant: 'destructive' }),
  });
  const webcalLink = icsTokenData?.icsToken
    ? `webcal://${window.location.host}/api/calendar/feed/creators/${creatorId}.ics?token=${icsTokenData.icsToken}`
    : null;

  // Create new category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const response = await apiRequest('POST', '/api/categories', data);
      return await response.json();
    },
    onSuccess: (newCategory: any) => {
      toast({
        title: 'Category Created',
        description: `New category "${newCategory.name}" has been created successfully.`,
      });
      setIsCreatingCategory(false);
      setNewCategory({ name: '', description: '' });
      // Refresh categories list
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      // Auto-select the new category in the class form
      setNewClass(prev => ({ ...prev, categoryId: newCategory.id }));
    },
    onError: (error: any) => {
      toast({
        title: 'Category Creation Failed',
        description: error.message || 'Failed to create category. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Create new class mutation
  const createClassMutation = useMutation({
    mutationFn: async (data: ClassPayload) => {
      const response = await apiRequest('POST', '/api/classes', data);
      const result = (await response.json()) as ClassMutationResponse;
      let thumbnailUploaded = false;
      let thumbnailUploadFailed = false;

      if (classThumbnailFile) {
        try {
          const uploadResult = await uploadClassThumbnail(result.class.id, classThumbnailFile);
          result.class = uploadResult.class;
          result.imageUrl = uploadResult.imageUrl;
          thumbnailUploaded = true;
        } catch {
          thumbnailUploadFailed = true;
        }
      }

      return {
        ...result,
        thumbnailUploaded,
        thumbnailUploadFailed,
      };
    },
    onSuccess: ({
      thumbnailUploaded,
      thumbnailUploadFailed,
    }: ClassMutationResponse & { thumbnailUploaded: boolean; thumbnailUploadFailed: boolean }) => {
      toast({
        title: 'Class Created',
        description: thumbnailUploadFailed
          ? 'Your class was created, but the thumbnail upload failed.'
          : thumbnailUploaded
            ? 'Your new class and thumbnail have been saved.'
            : 'Your new class has been successfully created.',
        variant: thumbnailUploadFailed ? 'destructive' : 'default',
      });
      setIsAddingClass(false);
      resetClassEditor();
      // Invalidate both queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['/api/creator/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
    },
    onError: error => {
      let description = 'Failed to create class. Please try again.';
      try {
        const match = (error as Error).message?.match(/^\d+: (.+)$/s);
        if (match) {
          const body = JSON.parse(match[1]);
          if (body.code === 'CONNECT_REQUIRED') {
            description =
              'You need to set up your payout account before creating paid classes. Go to the Payouts tab in your dashboard.';
          } else if (body.error === 'Service unavailable') {
            description = 'The server is starting up. Please wait a moment and try again.';
          } else if (body.error) {
            description = body.error;
          }
        }
      } catch {
        // ignore parse errors, use default
      }
      toast({ title: 'Creation Failed', description, variant: 'destructive' });
    },
  });

  // Delete class mutation
  const deleteClassMutation = useMutation({
    mutationFn: async (classId: string) => {
      return await apiRequest('DELETE', `/api/classes/${classId}`);
    },
    onSuccess: () => {
      toast({
        title: 'Class Deleted',
        description: 'The class has been successfully deleted.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
    },
    onError: _error => {
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete class. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update class mutation
  const updateClassMutation = useMutation({
    mutationFn: async ({ classId, data }: { classId: string; data: ClassPayload }) => {
      const response = await apiRequest('PUT', `/api/classes/${classId}`, data);
      const result = (await response.json()) as ClassMutationResponse;
      let thumbnailUploaded = false;
      let thumbnailUploadFailed = false;

      if (classThumbnailFile) {
        try {
          const uploadResult = await uploadClassThumbnail(classId, classThumbnailFile);
          result.class = uploadResult.class;
          result.imageUrl = uploadResult.imageUrl;
          thumbnailUploaded = true;
        } catch {
          thumbnailUploadFailed = true;
        }
      }

      return {
        ...result,
        thumbnailUploaded,
        thumbnailUploadFailed,
      };
    },
    onSuccess: ({
      thumbnailUploaded,
      thumbnailUploadFailed,
    }: ClassMutationResponse & { thumbnailUploaded: boolean; thumbnailUploadFailed: boolean }) => {
      toast({
        title: 'Class Updated',
        description: thumbnailUploadFailed
          ? 'Your class was updated, but the thumbnail upload failed.'
          : thumbnailUploaded
            ? 'Your class details and thumbnail have been updated.'
            : 'Your class has been successfully updated.',
        variant: thumbnailUploadFailed ? 'destructive' : 'default',
      });
      setIsAddingClass(false);
      resetClassEditor();
      queryClient.invalidateQueries({ queryKey: ['/api/creator/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
    },
    onError: _error => {
      toast({
        title: 'Update Failed',
        description: 'Failed to update class. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const togglePublishMutation = useMutation({
    mutationFn: async ({ classId, publish }: { classId: string; publish: boolean }) => {
      const response = await apiRequest('PUT', `/api/classes/${classId}`, {
        isPublished: publish,
      });
      return response.json();
    },
    onSuccess: (_data, { publish }) => {
      toast({
        title: publish ? 'Class Published' : 'Class Unpublished',
        description: publish
          ? 'Your class is now live and visible to students.'
          : 'Your class has been taken offline.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
    },
    onError: (_error, { publish }) => {
      toast({
        title: publish ? 'Publish Failed' : 'Unpublish Failed',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const unpublishAndCancelSessionsMutation = useMutation({
    mutationFn: async ({
      classId,
      anchorSessionId,
    }: {
      classId: string;
      anchorSessionId: string;
    }) => {
      // Cancels this session and every future one on the class in a single call
      // (scope: 'forward'), refunding any paid attendees — same path as the
      // per-session "Cancel" button in the Sessions dialog.
      await apiRequest('PATCH', `/api/classes/${classId}/sessions/${anchorSessionId}/cancel`, {
        scope: 'forward',
        note: 'Class unpublished',
      });
      const response = await apiRequest('PUT', `/api/classes/${classId}`, {
        isPublished: false,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Class Unpublished',
        description: 'Your class is offline and its scheduled sessions were cancelled.',
      });
      setUnpublishConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['/api/creator/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/schedule'] });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/archive'] });
    },
    onError: () => {
      toast({
        title: 'Unpublish Failed',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update creator profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest('PUT', '/api/creator/profile', data);
    },
    onSuccess: () => {
      toast({
        title: 'Profile Updated',
        description: 'Your creator profile has been successfully updated.',
      });
      setIsEditingProfile(false);
      queryClient.invalidateQueries({ queryKey: ['/api/creator/profile'] });
    },
    onError: _error => {
      toast({
        title: 'Update Failed',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Archive class mutation
  const archiveClassMutation = useMutation({
    mutationFn: async (classId: string) => {
      return await apiRequest('PUT', `/api/classes/${classId}/archive`);
    },
    onSuccess: (data: any, classId: string) => {
      toast({
        title: 'Class Archived',
        description: 'The class has been successfully archived and hidden from public view.',
      });

      // Update the creator classes cache
      queryClient.setQueryData(['/api/creator/classes'], (oldClasses: any[]) => {
        if (!oldClasses) return oldClasses;
        return oldClasses.map((cls: any) =>
          cls.id === classId ? { ...cls, isArchived: true } : cls
        );
      });

      // Invalidate public classes cache
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
    },
    onError: _error => {
      toast({
        title: 'Archive Failed',
        description: 'Failed to archive class. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Unarchive class mutation
  const unarchiveClassMutation = useMutation({
    mutationFn: async (classId: string) => {
      return await apiRequest('PUT', `/api/classes/${classId}/unarchive`);
    },
    onSuccess: (data: any, classId: string) => {
      toast({
        title: 'Class Unarchived',
        description: 'The class has been successfully unarchived and is now visible to the public.',
      });

      // Update the creator classes cache
      queryClient.setQueryData(['/api/creator/classes'], (oldClasses: any[]) => {
        if (!oldClasses) return oldClasses;
        return oldClasses.map((cls: any) =>
          cls.id === classId ? { ...cls, isArchived: false } : cls
        );
      });

      // Invalidate public classes cache
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
    },
    onError: _error => {
      toast({
        title: 'Unarchive Failed',
        description: 'Failed to unarchive class. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Session management mutations
  const createSessionMutation = useMutation({
    mutationFn: async ({ classId, sessionData }: { classId: string; sessionData: any }) => {
      return await apiRequest('POST', `/api/classes/${classId}/sessions`, sessionData);
    },
    onSuccess: () => {
      toast({
        title: 'Session Created',
        description: 'Class session has been successfully created.',
      });
      setEditingSession(null);
      setNewSession({
        ...getDefaultSessionTimes(),
        maxStudents: '',
        notes: '',
        disablePostClassEmail: false,
        isRecurring: false,
        recurrenceFreq: 'weekly',
        recurrenceDays: [],
        recurrenceEndsAt: '',
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/classes/${managingSessionsFor?.id}/sessions`],
      });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/schedule'] });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/archive'] });
    },
    onError: _error => {
      toast({
        title: 'Creation Failed',
        description: 'Failed to create session. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const cancelSessionMutation = useMutation({
    mutationFn: async ({
      sessionId,
      scope,
      note,
      feeBearer,
    }: {
      sessionId: string;
      scope: string;
      note?: string;
      feeBearer?: 'customer' | 'creator';
    }) => {
      const classId = managingSessionsFor?.id;
      if (!classId) throw new Error('No class selected');
      return await apiRequest('PATCH', `/api/classes/${classId}/sessions/${sessionId}/cancel`, {
        scope,
        note,
        feeBearer,
      });
    },
    onSuccess: () => {
      toast({
        title: cancelScope === 'forward' ? 'Sessions Cancelled' : 'Session Cancelled',
        description:
          cancelScope === 'forward'
            ? 'This session and all future sessions have been cancelled.'
            : 'The session has been cancelled.',
      });
      setCancelingSession(null);
      setCancelNote('');
      queryClient.invalidateQueries({
        queryKey: [`/api/classes/${managingSessionsFor?.id}/sessions`],
      });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/schedule'] });
    },
    onError: () => {
      toast({
        title: 'Cancel Failed',
        description: 'Failed to cancel session.',
        variant: 'destructive',
      });
    },
  });

  // Update session mutation
  const updateSessionMutation = useMutation({
    mutationFn: async ({ sessionId, sessionData }: { sessionId: string; sessionData: any }) => {
      return await apiRequest('PUT', `/api/sessions/${sessionId}`, sessionData);
    },
    onSuccess: () => {
      toast({
        title: 'Session Updated',
        description: 'Class session has been successfully updated.',
      });
      setEditingSession(null);
      setNewSession({
        ...getDefaultSessionTimes(),
        maxStudents: '',
        notes: '',
        disablePostClassEmail: false,
        isRecurring: false,
        recurrenceFreq: 'weekly',
        recurrenceDays: [],
        recurrenceEndsAt: '',
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/classes/${managingSessionsFor?.id}/sessions`],
      });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/schedule'] });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/archive'] });
    },
    onError: _error => {
      toast({
        title: 'Update Failed',
        description: 'Failed to update session. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Review refund request mutation
  const reviewRefundMutation = useMutation({
    mutationFn: async ({
      refundRequestId,
      action,
      denialReason,
      feeBearer,
    }: {
      refundRequestId: string;
      action: 'approve' | 'deny';
      denialReason?: string;
      feeBearer?: 'customer' | 'creator';
    }) => {
      const response = await apiRequest(`POST`, `/api/refunds/requests/${refundRequestId}/review`, {
        action,
        denialReason,
        feeBearer,
      });
      return await response.json();
    },
    onSuccess: (_data, { action }) => {
      toast({
        title: action === 'approve' ? 'Refund Approved' : 'Refund Denied',
        description:
          action === 'approve'
            ? 'The refund has been approved and will be processed.'
            : 'The refund request has been denied.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/refunds/requests/creator'] });
    },
    onError: _error => {
      toast({
        title: 'Review Failed',
        description: 'Failed to process refund review. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const cancelTicketMutation = useMutation({
    mutationFn: async ({
      orderId,
      feeBearer,
    }: {
      orderId: string;
      feeBearer: 'customer' | 'creator';
    }) => {
      const response = await apiRequest('POST', `/api/refunds/orders/${orderId}/cancel`, {
        feeBearer,
      });
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Ticket canceled',
        description: 'The student has been refunded.',
      });
      setCancelingTicketOrderId(null);
      // Any cached roster/enrollment views are now stale (staleTime is Infinity).
      queryClient.invalidateQueries({
        predicate: query => {
          const key = String(query.queryKey[0] ?? '');
          return key.includes('/enrollments') || key.includes('/attendees');
        },
      });
      queryClient.invalidateQueries({ queryKey: ['/api/creator/schedule'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not cancel ticket',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const [broadcastClassId, setBroadcastClassId] = useState('');
  const [broadcastAudience, setBroadcastAudience] = useState<'all' | 'session'>('all');
  const [broadcastSessionId, setBroadcastSessionId] = useState('');
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');

  // Sessions a creator can broadcast to for the selected class (future + 2 past).
  const { data: broadcastSessionsData } = useQuery<{
    sessions: Array<{
      id: string;
      startDateTime: string;
      endDateTime: string;
      enrolledCount: number;
      isPast: boolean;
    }>;
  }>({
    queryKey: ['/api/creator/classes', broadcastClassId, 'broadcast-sessions'],
    queryFn: () => authedJson(`/api/creator/classes/${broadcastClassId}/broadcast-sessions`),
    enabled: !!broadcastClassId,
  });
  const broadcastSessions = broadcastSessionsData?.sessions ?? [];

  const broadcastMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/creator/classes/${broadcastClassId}/broadcast`, {
        audience: broadcastAudience,
        sessionId: broadcastAudience === 'session' ? broadcastSessionId : undefined,
        subject: broadcastSubject,
        body: broadcastBody,
      });
      return res.json();
    },
    onSuccess: (data: { queued: number; message?: string }) => {
      toast({
        title: 'Message sent',
        description: data.message ?? `Message sent to ${data.queued} student(s).`,
      });
      setBroadcastSubject('');
      setBroadcastBody('');
      setBroadcastSessionId('');
    },
    onError: (err: Error) =>
      toast({
        title: 'Could not send',
        description: err.message || 'Please try again.',
        variant: 'destructive',
      }),
  });

  // Fetch sessions for the class being managed
  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: [`/api/classes/${managingSessionsFor?.id}/sessions`],
    queryFn: async () => {
      if (!managingSessionsFor?.id) return [];
      return await authedJson(`/api/classes/${managingSessionsFor.id}/sessions`);
    },
    enabled: !!managingSessionsFor,
  });

  // Waitlist for the class being managed — who's asked to hear about a new session.
  const { data: waitlist, isLoading: waitlistLoading } = useQuery<{
    entries: Array<{ id: string; email: string; name: string | null; createdAt: string }>;
    total: number;
  }>({
    queryKey: [`/api/waitlist/classes/${managingSessionsFor?.id}`],
    queryFn: async () => {
      if (!managingSessionsFor?.id) return { entries: [], total: 0 };
      return await authedJson(`/api/waitlist/classes/${managingSessionsFor.id}`);
    },
    enabled: !!managingSessionsFor,
  });

  const [notifySessionId, setNotifySessionId] = useState('');

  // Per-class waitlist counts for the "My Classes" tiles — one call for all
  // of this creator's classes instead of one per card.
  const { data: waitlistCounts } = useQuery<{ counts: Record<string, number> }>({
    queryKey: ['/api/waitlist/counts'],
    queryFn: async () => await authedJson('/api/waitlist/counts'),
  });

  const notifyWaitlistMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiRequest(
        'POST',
        `/api/waitlist/classes/${managingSessionsFor?.id}/notify`,
        { sessionId }
      );
      return await response.json();
    },
    onSuccess: data => {
      toast({
        title: 'Waitlist notified',
        description: `Let ${data.notifiedCount} ${data.notifiedCount === 1 ? 'person' : 'people'} know about the new session.`,
      });
      setNotifySessionId('');
      queryClient.invalidateQueries({
        queryKey: [`/api/waitlist/classes/${managingSessionsFor?.id}`],
      });
    },
    onError: () => {
      toast({
        title: 'Could not notify waitlist',
        description: 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Get all upcoming sessions from all classes for the session filter dropdown
  const allUpcomingSessions = React.useMemo(() => {
    if (!classes || !Array.isArray(classes)) return [];

    const sessions: any[] = [];
    classes.forEach((classItem: any) => {
      if (classItem.sessions && Array.isArray(classItem.sessions)) {
        classItem.sessions.forEach((session: any) => {
          // Only include future sessions
          if (new Date(session.startDateTime) > new Date()) {
            sessions.push({
              ...session,
              className: classItem.title,
              classId: classItem.id,
            });
          }
        });
      }
    });

    // Sort by start date
    return sessions.sort(
      (a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
    );
  }, [classes]);

  // Group enrollments by unique students and filter based on criteria
  const filteredStudents = React.useMemo(() => {
    const now = new Date();

    // Group enrollments by student
    const studentGroups = new Map();
    const enrollmentRows = Array.isArray(enrollments) ? enrollments : [];
    enrollmentRows.forEach((enrollment: any) => {
      const key = `${enrollment.studentEmail}_${enrollment.studentName}`;
      if (!studentGroups.has(key)) {
        studentGroups.set(key, {
          studentName: enrollment.studentName,
          studentEmail: enrollment.studentEmail,
          studentPhone: enrollment.studentPhone,
          enrollments: [],
          totalClasses: 0,
          totalAmount: 0,
          hasActiveSessions: false,
          latestEnrollmentDate: null,
        });
      }

      const student = studentGroups.get(key);
      student.enrollments.push(enrollment);
      student.totalClasses++;
      student.totalAmount += enrollment.totalAmount || 0;

      // Check if this enrollment has an active session
      if (enrollment.sessionStartDateTime && new Date(enrollment.sessionStartDateTime) > now) {
        student.hasActiveSessions = true;
      }

      // Track latest enrollment date
      const enrollmentDate = new Date(enrollment.enrolledAt);
      if (!student.latestEnrollmentDate || enrollmentDate > student.latestEnrollmentDate) {
        student.latestEnrollmentDate = enrollmentDate;
      }
    });

    if (Array.isArray(manualStudents)) {
      manualStudents.forEach((student: any) => {
        const key = `${student.email}_${student.name}`;
        if (!studentGroups.has(key)) {
          studentGroups.set(key, {
            studentName: student.name,
            studentEmail: student.email,
            studentPhone: student.phoneNumber,
            enrollments: [],
            totalClasses: 0,
            totalAmount: 0,
            hasActiveSessions: false,
            latestEnrollmentDate: student.createdAt ? new Date(student.createdAt) : null,
            isManualStudent: true,
            notes: student.notes,
          });
        }
      });
    }

    let filteredStudents = Array.from(studentGroups.values());

    // Filter by student status (active vs all)
    if (studentStatusFilter === 'active') {
      filteredStudents = filteredStudents.filter(student => student.hasActiveSessions);
    }

    // Filter by session if not "all"
    if (selectedSessionFilter !== 'all') {
      filteredStudents = filteredStudents.filter(student =>
        student.enrollments.some(
          (enrollment: any) => enrollment.sessionId === selectedSessionFilter
        )
      );
    }

    // Filter by search term if provided
    if (studentSearchTerm.trim() !== '') {
      const searchTerm = studentSearchTerm.toLowerCase();
      filteredStudents = filteredStudents.filter(student => {
        // Search in student info
        const nameMatch = student.studentName?.toLowerCase().includes(searchTerm);
        const emailMatch = student.studentEmail?.toLowerCase().includes(searchTerm);

        // Search in class names
        const classMatch = student.enrollments.some((enrollment: any) =>
          enrollment.className?.toLowerCase().includes(searchTerm)
        );

        return nameMatch || emailMatch || classMatch;
      });
    }

    // Sort students by latest enrollment date (most recent first)
    filteredStudents.sort((a, b) => {
      if (!a.latestEnrollmentDate) return 1;
      if (!b.latestEnrollmentDate) return -1;
      return b.latestEnrollmentDate.getTime() - a.latestEnrollmentDate.getTime();
    });

    return filteredStudents;
  }, [enrollments, manualStudents, selectedSessionFilter, studentSearchTerm, studentStatusFilter]);

  const handleCreateSession = (e: React.FormEvent) => {
    e.preventDefault();
    if (!managingSessionsFor) return;

    const sessionData = {
      ...newSession,
      startDateTime: new Date(newSession.startDateTime).toISOString(),
      endDateTime: new Date(newSession.endDateTime).toISOString(),
      maxStudents: newSession.maxStudents === '' ? null : parseInt(newSession.maxStudents),
    };

    if (editingSession) {
      updateSessionMutation.mutate({ sessionId: editingSession.id, sessionData });
    } else {
      // If recurring, save recurrence rule + start time + max students on the
      // class so the generator can produce matching future sessions.
      if (newSession.isRecurring && newSession.startDateTime) {
        // Store the wall-clock time as typed (local), not UTC: the generator
        // resolves the UTC offset per date so DST doesn't shift the class.
        const startDate = new Date(newSession.startDateTime);
        const hh = String(startDate.getHours()).padStart(2, '0');
        const mm = String(startDate.getMinutes()).padStart(2, '0');
        const recurrenceRule = buildRRule(newSession.recurrenceFreq, newSession.recurrenceDays);
        updateClassMutation.mutate({
          classId: managingSessionsFor.id,
          data: {
            recurrenceRule,
            recurrenceStartTime: `${hh}:${mm}`,
            recurrenceEndsAt: newSession.recurrenceEndsAt
              ? new Date(newSession.recurrenceEndsAt).toISOString()
              : null,
            maxStudents: newSession.maxStudents === '' ? null : parseInt(newSession.maxStudents),
          } as any,
        });
      }
      createSessionMutation.mutate({
        classId: managingSessionsFor.id,
        sessionData,
      });
    }
  };

  const handleCreateCategory = (e: React.FormEvent) => {
    e.preventDefault();
    createCategoryMutation.mutate({
      name: newCategory.name,
      description: newCategory.description || undefined,
    });
  };

  const handleCreateClass = (e: React.FormEvent) => {
    e.preventDefault();
    const recurrenceRule = newClass.isRecurring
      ? buildRRule(newClass.recurrenceFreq, newClass.recurrenceDays)
      : null;
    const classData: ClassPayload = {
      title: newClass.title,
      description: newClass.description,
      price: newClass.pricingMode === 'free' ? 0 : parseFloat(newClass.price) || 0,
      categoryId: newClass.categoryId,
      duration: parseInt(newClass.duration),
      cancellationPolicy: newClass.cancellationPolicy,
      registrationCutoffHours: registrationCutoffFromFormValue(newClass.registrationCutoff),
      registrationCutoffOnlyIfEmpty: newClass.registrationCutoffOnlyIfEmpty,
      deliveryMode: newClass.deliveryMode,
      localityId: newClass.localityId || null,
      pricingMode: newClass.pricingMode,
      suggestedPrice:
        newClass.pricingMode === 'pay_what_you_want' && newClass.suggestedPrice
          ? parseFloat(newClass.suggestedPrice)
          : null,
      promoEnabled: newClass.promoEnabled,
      promoPrice:
        newClass.promoEnabled && newClass.promoPrice !== ''
          ? parseFloat(newClass.promoPrice)
          : null,
      // Optional promo window (datetime-local → ISO). Empty = always-on.
      promoStartsAt:
        newClass.promoEnabled && newClass.promoStartsAt
          ? new Date(newClass.promoStartsAt).toISOString()
          : null,
      promoEndsAt:
        newClass.promoEnabled && newClass.promoEndsAt
          ? new Date(newClass.promoEndsAt).toISOString()
          : null,
      recurrenceRule,
      recurrenceEndsAt:
        newClass.isRecurring && newClass.recurrenceEndsAt
          ? new Date(newClass.recurrenceEndsAt).toISOString()
          : null,
      recurrenceStartTime:
        newClass.isRecurring && newClass.recurrenceStartTime ? newClass.recurrenceStartTime : null,
      passEnabled: newClass.passEnabled,
      passSessionCount:
        newClass.passEnabled && newClass.passSessionCount
          ? parseInt(newClass.passSessionCount)
          : null,
      passPrice:
        newClass.passEnabled && newClass.passPrice
          ? Math.round(parseFloat(newClass.passPrice) * 100)
          : null,
      passExpiryDays:
        newClass.passEnabled && newClass.passExpiryDays ? parseInt(newClass.passExpiryDays) : null,
      venueId: newClass.venueId || null,
      livestreamClassId: newClass.livestreamClassId || null,
      feedbackEmailEnabled: newClass.feedbackEmailEnabled,
      requiresWaiver: newClass.requiresWaiver,
      waiverTitle: newClass.requiresWaiver
        ? newClass.waiverTitle.trim() || 'Liability Waiver'
        : null,
      waiverText: newClass.requiresWaiver ? newClass.waiverText.trim() : null,
      waiverVersion: Math.max(parseInt(newClass.waiverVersion, 10) || 1, 1),
    };

    if (newClass.requiresWaiver && !newClass.waiverText.trim()) {
      toast({
        title: 'Waiver text required',
        description: 'Add the waiver language students must review and sign.',
        variant: 'destructive',
      });
      return;
    }

    if (editingClass) {
      updateClassMutation.mutate({ classId: editingClass.id, data: classData });
    } else {
      createClassMutation.mutate(classData);
    }
  };

  const handleUpdateProfile = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileForm);
  };

  if (!user || (user.role !== 'creator' && user.role !== 'admin')) {
    return (
      <PublicShell routeKey="creator-dashboard">
        <div className="page-shell py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground">
            This page is only available to creators and administrators.
          </p>
        </div>
      </PublicShell>
    );
  }

  const creatorProfile = creator as
    | {
        displayName?: string | null;
        bio?: string | null;
        creatorHeadline?: string | null;
        profileImageUrl?: string | null;
      }
    | undefined;
  const creatorProfileImageUrl =
    creatorProfile?.profileImageUrl && !profileImageFailed
      ? normalizeImageUrl(creatorProfile.profileImageUrl, '')
      : null;
  const creatorClasses = Array.isArray(classes) ? (classes as any[]) : [];
  const creatorSchedule = Array.isArray(schedule) ? (schedule as any[]) : [];
  const hasUsableProfile = Boolean(
    creatorProfile?.displayName?.trim() &&
      (creatorProfile.bio?.trim() || creatorProfile.creatorHeadline?.trim())
  );
  const hasClass = creatorClasses.length > 0;
  // "Ever scheduled a session" — counts past/archived sessions too (via the
  // all-time totalSessionCount), so a creator whose sessions have all happened
  // doesn't get stuck on this step.
  const hasSession =
    creatorSchedule.length > 0 ||
    creatorClasses.some(
      classItem =>
        (Array.isArray(classItem.sessions) && classItem.sessions.length > 0) ||
        Number(classItem.totalSessionCount) > 0
    );
  const hasPublishedClass = creatorClasses.some(classItem => classItem.isPublished === true);
  const hasClassRequiringPayoutOnboarding = creatorClasses.some(classRequiresPayoutOnboarding);
  const shouldIncludePayoutOnboardingItem = !hasClass || hasClassRequiringPayoutOnboarding;
  const payoutOnboardingComplete = Boolean(
    connectStatus?.chargesEnabled || connectStatus?.onboardingStatus === 'complete'
  );
  const onboardingItems = [
    {
      label: 'Complete public creator profile',
      complete: hasUsableProfile,
      helper:
        'Add a clear name, headline, and short bio so students know who they are learning from.',
    },
    ...(shouldIncludePayoutOnboardingItem
      ? [
          {
            label: 'Set up payouts for paid classes',
            complete: payoutOnboardingComplete,
            helper:
              'Free classes can be created without this. Paid classes need Stripe Connect first.',
            action:
              connectStatus && !payoutOnboardingComplete
                ? () => connectOnboardMutation.mutate()
                : undefined,
            actionLabel:
              connectStatus?.onboardingStatus === 'in_progress'
                ? 'Resume payouts'
                : 'Set up payouts',
          },
        ]
      : []),
    {
      label: 'Create your first class',
      complete: hasClass,
      helper: 'Draft the class students will browse and book.',
      action: !hasClass ? () => setIsAddingClass(true) : undefined,
      actionLabel: 'Create class',
    },
    {
      label: 'Add at least one scheduled session',
      complete: hasSession,
      helper: 'Students book specific dates and times, so every class needs at least one session.',
    },
    {
      label: 'Publish when ready',
      complete: hasPublishedClass,
      helper: 'Published classes appear in browse, calendar, search, creator, and location views.',
    },
  ];
  const incompleteOnboardingItems = onboardingItems.filter(item => !item.complete);
  // Don't render the checklist until the data it depends on has loaded — otherwise
  // a fully-onboarded creator sees it flash (all items look incomplete) then vanish.
  const onboardingDataLoading =
    classesLoading || scheduleLoading || creatorProfileLoading || connectStatusLoading;

  // Free classes have no payment/processing fee, so the who-pays-the-fee choice
  // does not apply when cancelling their sessions.
  const cancelClassIsFree = Boolean(
    managingSessionsFor &&
      (managingSessionsFor.pricingMode === 'free' || Number(managingSessionsFor.price) === 0)
  );

  return (
    <PublicShell routeKey="creator-dashboard">
      <div className="page-shell py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Creator Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Manage your classes and view student enrollments
            </p>
          </div>
          <Dialog
            open={isAddingClass}
            onOpenChange={open => {
              setIsAddingClass(open);
              if (!open) {
                // Reset form and editing state when dialog closes
                resetClassEditor();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="flex items-center gap-2"
                data-testid="button-add-class"
                onClick={() => {
                  // Ensure we're in create mode when clicking "Add New Class"
                  resetClassEditor();
                }}
              >
                <PlusCircle size={16} />
                Add New Class
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md flex flex-col max-h-[90dvh] p-0">
              <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
                <DialogTitle>{editingClass ? 'Edit Class' : 'Create New Class'}</DialogTitle>
                <DialogDescription>
                  {editingClass
                    ? 'Update your existing class details'
                    : 'Add a new class to your teaching portfolio'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateClass} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto overscroll-y-contain px-6 py-4 space-y-4">
                  <div>
                    <Label htmlFor="title">Class Title</Label>
                    <Input
                      id="title"
                      value={newClass.title}
                      onChange={e => setNewClass({ ...newClass, title: e.target.value })}
                      required
                      data-testid="input-class-title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={newClass.description}
                      onChange={e => setNewClass({ ...newClass, description: e.target.value })}
                      rows={6}
                      placeholder="Class description, agenda, materials, and what students can expect"
                      required
                      data-testid="input-class-description"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pricing-mode">Pricing</Label>
                    <Select
                      value={newClass.pricingMode}
                      onValueChange={value =>
                        setNewClass(c => ({
                          ...c,
                          pricingMode: value as PricingMode,
                          price: value === 'free' ? '0' : c.price,
                        }))
                      }
                    >
                      <SelectTrigger id="pricing-mode" data-testid="select-class-pricing-mode">
                        <SelectValue placeholder="Select pricing type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRICING_MODES.map(mode => (
                          <SelectItem key={mode} value={mode}>
                            {PRICING_MODE_LABELS[mode]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {newClass.pricingMode !== 'free' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="price">
                          {newClass.pricingMode === 'pay_what_you_want'
                            ? 'Suggested Price ($)'
                            : 'Price ($)'}
                        </Label>
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          min="0"
                          value={
                            newClass.pricingMode === 'pay_what_you_want'
                              ? newClass.suggestedPrice
                              : newClass.price
                          }
                          onChange={e =>
                            newClass.pricingMode === 'pay_what_you_want'
                              ? setNewClass({ ...newClass, suggestedPrice: e.target.value })
                              : setNewClass({ ...newClass, price: e.target.value })
                          }
                          required={newClass.pricingMode === 'standard'}
                          placeholder={
                            newClass.pricingMode === 'pay_what_you_want' ? 'Optional' : ''
                          }
                          data-testid="input-class-price"
                        />
                      </div>
                    </div>
                  )}
                  {newClass.pricingMode === 'standard' && (
                    <div className="space-y-3 border rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="promo-enabled"
                          title="Set promo price"
                          checked={newClass.promoEnabled}
                          onChange={e =>
                            setNewClass(c => ({ ...c, promoEnabled: e.target.checked }))
                          }
                          className="h-4 w-4"
                          data-testid="checkbox-promo-enabled"
                        />
                        <Label htmlFor="promo-enabled" className="cursor-pointer font-medium">
                          Set promo price
                        </Label>
                      </div>
                      {newClass.promoEnabled && (
                        <div className="pt-1">
                          <Label htmlFor="promo-price">Promo price ($)</Label>
                          <Input
                            id="promo-price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={newClass.promoPrice}
                            onChange={e => setNewClass(c => ({ ...c, promoPrice: e.target.value }))}
                            placeholder="e.g. 0 for free, or a discounted price"
                            required={newClass.promoEnabled}
                            data-testid="input-promo-price"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            The full price shows crossed out next to this discounted price. Enter 0
                            to offer the class free.
                          </p>
                          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor="promo-starts">Promo starts (optional)</Label>
                              <Input
                                id="promo-starts"
                                type="datetime-local"
                                value={newClass.promoStartsAt}
                                onChange={e =>
                                  setNewClass(c => ({ ...c, promoStartsAt: e.target.value }))
                                }
                                data-testid="input-promo-starts"
                              />
                            </div>
                            <div>
                              <Label htmlFor="promo-ends">Promo ends (optional)</Label>
                              <Input
                                id="promo-ends"
                                type="datetime-local"
                                value={newClass.promoEndsAt}
                                onChange={e =>
                                  setNewClass(c => ({ ...c, promoEndsAt: e.target.value }))
                                }
                                data-testid="input-promo-ends"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Optional date range — the promo applies to{' '}
                            <strong>sessions that fall inside the window</strong> (by session date,
                            not purchase date). Leave both blank to apply the promo to every
                            session.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="delivery-mode">Delivery Mode</Label>
                      <Select
                        value={newClass.deliveryMode}
                        onValueChange={value =>
                          setNewClass(current => ({
                            ...current,
                            deliveryMode: value as DeliveryMode,
                            localityId: requiresLocalityForDeliveryMode(value as DeliveryMode)
                              ? current.localityId
                              : '',
                          }))
                        }
                      >
                        <SelectTrigger id="delivery-mode" data-testid="select-class-delivery-mode">
                          <SelectValue placeholder="Select how students attend" />
                        </SelectTrigger>
                        <SelectContent>
                          {DELIVERY_MODES.map(mode => (
                            <SelectItem key={mode} value={mode}>
                              {DELIVERY_MODE_LABELS[mode]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {requiresLocalityForDeliveryMode(newClass.deliveryMode) && (
                    <>
                      <div>
                        <Label htmlFor="class-locality">Locality</Label>
                        <Select
                          value={newClass.localityId}
                          onValueChange={value =>
                            setNewClass({ ...newClass, localityId: value, venueId: '' })
                          }
                        >
                          <SelectTrigger id="class-locality" data-testid="select-class-locality">
                            <SelectValue placeholder="Select the rooted locality" />
                          </SelectTrigger>
                          <SelectContent>
                            {localities.map(locality => (
                              <SelectItem key={locality.id} value={locality.id}>
                                {locality.name} ({locality.regionName})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {newClass.localityId && venues.length > 0 && (
                        <div>
                          <Label htmlFor="class-venue">Venue (optional)</Label>
                          <Select
                            value={newClass.venueId || '__none'}
                            onValueChange={value =>
                              setNewClass({ ...newClass, venueId: value === '__none' ? '' : value })
                            }
                          >
                            <SelectTrigger id="class-venue">
                              <SelectValue placeholder="Select a venue / space" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">No specific venue</SelectItem>
                              {venues.map(venue => (
                                <SelectItem key={venue.id} value={venue.id}>
                                  {venue.name}
                                  {venue.address ? ` — ${venue.address}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div>
                        <Label htmlFor="class-livestream">Online livestream class (optional)</Label>
                        <Select
                          value={newClass.livestreamClassId || '__none'}
                          onValueChange={value =>
                            setNewClass({
                              ...newClass,
                              livestreamClassId: value === '__none' ? '' : value,
                            })
                          }
                        >
                          <SelectTrigger
                            id="class-livestream"
                            data-testid="select-livestream-class"
                          >
                            <SelectValue placeholder="Link an online version of this class" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">No linked online class</SelectItem>
                            {(Array.isArray(classes) ? classes : [])
                              .filter(
                                (c: any) =>
                                  c.deliveryMode === 'online' &&
                                  !c.isArchived &&
                                  c.id !== editingClass?.id
                              )
                              .map((c: any) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.title}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Shown on this class&apos;s page so students can join online when it&apos;s
                          sold out or they can&apos;t make it in person.
                        </p>
                      </div>
                    </>
                  )}
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={newClass.categoryId}
                      onValueChange={value => {
                        if (value === 'create-new') {
                          setIsCreatingCategory(true);
                        } else {
                          setNewClass({ ...newClass, categoryId: value });
                        }
                      }}
                      required
                    >
                      <SelectTrigger data-testid="select-class-category">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories?.map((category: any) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="create-new" className="font-medium text-primary">
                          + Create New Category
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="duration">Duration (minutes)</Label>
                    <Input
                      id="duration"
                      type="number"
                      min="1"
                      step="1"
                      value={newClass.duration}
                      onChange={e => setNewClass({ ...newClass, duration: e.target.value })}
                      placeholder="e.g., 60"
                      required
                      data-testid="input-class-duration"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Enter the total length in minutes — e.g., 60 for 1 hour, 90 for 1½ hours
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="cancellation-policy">Cancellation Policy</Label>
                    <Select
                      value={newClass.cancellationPolicy}
                      onValueChange={value =>
                        setNewClass({ ...newClass, cancellationPolicy: value })
                      }
                    >
                      <SelectTrigger data-testid="select-class-cancellation-policy">
                        <SelectValue placeholder="Select a cancellation policy" />
                      </SelectTrigger>
                      <SelectContent>
                        {CANCELLATION_POLICY_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {
                        CANCELLATION_POLICY_OPTIONS.find(
                          option => option.value === newClass.cancellationPolicy
                        )?.description
                      }
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="registration-cutoff">Registration Cutoff</Label>
                    <Select
                      value={newClass.registrationCutoff}
                      onValueChange={value =>
                        setNewClass({ ...newClass, registrationCutoff: value })
                      }
                    >
                      <SelectTrigger
                        id="registration-cutoff"
                        data-testid="select-class-registration-cutoff"
                      >
                        <SelectValue placeholder="Select a registration cutoff" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGISTRATION_CUTOFF_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Online booking closes this long before each session starts.
                    </p>
                    {newClass.registrationCutoff !== NO_REGISTRATION_CUTOFF && (
                      <div className="mt-2 flex items-start gap-2">
                        <input
                          type="checkbox"
                          id="registration-cutoff-only-if-empty"
                          className="mt-1"
                          checked={newClass.registrationCutoffOnlyIfEmpty}
                          onChange={e =>
                            setNewClass({
                              ...newClass,
                              registrationCutoffOnlyIfEmpty: e.target.checked,
                            })
                          }
                          data-testid="checkbox-class-registration-cutoff-only-if-empty"
                        />
                        <Label
                          htmlFor="registration-cutoff-only-if-empty"
                          className="font-normal leading-snug"
                        >
                          Only close if nobody has signed up yet
                          <span className="block text-sm text-muted-foreground">
                            Once someone is coming, late sign-ups stay open until the session
                            starts.
                          </span>
                        </Label>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 border rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="requires-waiver"
                        title="Require liability waiver"
                        checked={newClass.requiresWaiver}
                        onChange={e =>
                          setNewClass(c => ({ ...c, requiresWaiver: e.target.checked }))
                        }
                        className="h-4 w-4"
                        data-testid="checkbox-requires-waiver"
                      />
                      <Label htmlFor="requires-waiver" className="cursor-pointer font-medium">
                        Require liability waiver
                      </Label>
                    </div>
                    {newClass.requiresWaiver && (
                      <div className="space-y-3 pt-1">
                        <div>
                          <Label htmlFor="waiver-title">Waiver title</Label>
                          <Input
                            id="waiver-title"
                            value={newClass.waiverTitle}
                            onChange={e =>
                              setNewClass(c => ({ ...c, waiverTitle: e.target.value }))
                            }
                            placeholder="Liability Waiver"
                            data-testid="input-waiver-title"
                          />
                        </div>
                        <div>
                          <Label htmlFor="waiver-text">Waiver text</Label>
                          <Textarea
                            id="waiver-text"
                            value={newClass.waiverText}
                            onChange={e => setNewClass(c => ({ ...c, waiverText: e.target.value }))}
                            rows={10}
                            placeholder="Paste the waiver language students must review and sign."
                            required={newClass.requiresWaiver}
                            data-testid="input-waiver-text"
                          />
                        </div>
                        <div>
                          <Label htmlFor="waiver-version">Waiver version</Label>
                          <Input
                            id="waiver-version"
                            type="number"
                            min="1"
                            step="1"
                            value={newClass.waiverVersion}
                            onChange={e =>
                              setNewClass(c => ({ ...c, waiverVersion: e.target.value }))
                            }
                            data-testid="input-waiver-version"
                          />
                          <p className="mt-1 text-xs text-muted-foreground">
                            Increase this number when the waiver language changes and returning
                            students need to sign the new version.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 border rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="feedback-email-enabled"
                        title="Send post-class feedback email"
                        checked={newClass.feedbackEmailEnabled}
                        onChange={e =>
                          setNewClass(c => ({ ...c, feedbackEmailEnabled: e.target.checked }))
                        }
                        className="h-4 w-4"
                      />
                      <Label
                        htmlFor="feedback-email-enabled"
                        className="cursor-pointer font-medium"
                      >
                        Send post-class feedback emails
                      </Label>
                    </div>
                  </div>
                  {/* Punch pass */}
                  <SharedPassGate classId={editingClass?.id}>
                    <div className="space-y-3 border rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="pass-enabled"
                          title="Offer punch passes"
                          checked={newClass.passEnabled}
                          onChange={e =>
                            setNewClass(c => ({ ...c, passEnabled: e.target.checked }))
                          }
                          className="h-4 w-4"
                        />
                        <Label htmlFor="pass-enabled" className="cursor-pointer font-medium">
                          Offer punch passes
                        </Label>
                      </div>
                      {newClass.passEnabled && (
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div>
                            <Label htmlFor="pass-session-count">Sessions per pass</Label>
                            <Input
                              id="pass-session-count"
                              type="number"
                              min="2"
                              max="100"
                              value={newClass.passSessionCount}
                              onChange={e =>
                                setNewClass(c => ({ ...c, passSessionCount: e.target.value }))
                              }
                              placeholder="e.g. 10"
                              required={newClass.passEnabled}
                            />
                          </div>
                          <div>
                            <Label htmlFor="pass-price">Pass price ($)</Label>
                            <Input
                              id="pass-price"
                              type="number"
                              min="0"
                              step="0.01"
                              value={newClass.passPrice}
                              onChange={e =>
                                setNewClass(c => ({ ...c, passPrice: e.target.value }))
                              }
                              placeholder="e.g. 80"
                              required={newClass.passEnabled}
                            />
                          </div>
                          <div className="col-span-2">
                            <Label htmlFor="pass-expiry">Expires after (days, optional)</Label>
                            <Input
                              id="pass-expiry"
                              type="number"
                              min="1"
                              value={newClass.passExpiryDays}
                              onChange={e =>
                                setNewClass(c => ({ ...c, passExpiryDays: e.target.value }))
                              }
                              placeholder="e.g. 90 — leave blank for no expiry"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </SharedPassGate>

                  <div className="space-y-3">
                    <Label htmlFor="class-thumbnail">Class Thumbnail</Label>
                    {classThumbnailPreview && (
                      <img
                        src={
                          classThumbnailPreview.startsWith('blob:')
                            ? classThumbnailPreview
                            : normalizeImageUrl(classThumbnailPreview, PLACEHOLDER.thumb)
                        }
                        alt="Class thumbnail preview"
                        className="h-40 w-full rounded-md object-cover border"
                        onError={imageErrorFallback(PLACEHOLDER.thumb, 'Class Thumbnail')}
                      />
                    )}
                    <Input
                      id="class-thumbnail"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={e => handleClassThumbnailChange(e.target.files?.[0] ?? null)}
                    />
                    <p className="text-sm text-muted-foreground">
                      Upload JPG, PNG, WebP, or GIF. Max size 5MB.
                    </p>
                  </div>
                </div>
                <DialogFooter className="px-6 py-4 border-t shrink-0">
                  <Button
                    type="submit"
                    disabled={createClassMutation.isPending || updateClassMutation.isPending}
                    data-testid="button-submit-class"
                  >
                    {editingClass
                      ? updateClassMutation.isPending
                        ? 'Updating...'
                        : 'Update Class'
                      : createClassMutation.isPending
                        ? 'Creating...'
                        : 'Create Class'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Create Category Dialog */}
          <Dialog open={isCreatingCategory} onOpenChange={setIsCreatingCategory}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Category</DialogTitle>
                <DialogDescription>
                  Add a new category that can be used for classes
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCategory} className="space-y-4">
                <div>
                  <Label htmlFor="categoryName">Category Name</Label>
                  <Input
                    id="categoryName"
                    value={newCategory.name}
                    onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                    placeholder="e.g., Music, Art, Technology"
                    required
                    data-testid="input-category-name"
                  />
                </div>
                <div>
                  <Label htmlFor="categoryDescription">Description (Optional)</Label>
                  <Textarea
                    id="categoryDescription"
                    value={newCategory.description}
                    onChange={e => setNewCategory({ ...newCategory, description: e.target.value })}
                    placeholder="Brief description of this category"
                    data-testid="input-category-description"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreatingCategory(false);
                      setNewCategory({ name: '', description: '' });
                    }}
                    data-testid="button-cancel-category"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createCategoryMutation.isPending}
                    data-testid="button-create-category"
                  >
                    {createCategoryMutation.isPending ? 'Creating...' : 'Create Category'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Session Management Dialog */}
          <Dialog open={!!managingSessionsFor} onOpenChange={() => setManagingSessionsFor(null)}>
            <DialogContent className="max-w-4xl max-h-[80dvh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Manage Sessions - {managingSessionsFor?.title}</DialogTitle>
                <DialogDescription>
                  Add, edit, and delete class sessions for this course.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-6">
                {/* Waitlist */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Waitlist</CardTitle>
                    <CardDescription>
                      People waiting to hear about a new session of this class.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {waitlistLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
                      </div>
                    ) : !waitlist || waitlist.total === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No one is on the waitlist for this class yet.
                      </p>
                    ) : (
                      <>
                        <p className="text-sm font-medium">
                          {waitlist.total} {waitlist.total === 1 ? 'person' : 'people'} waiting
                        </p>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {waitlist.entries.map((entry: any) => (
                            <div
                              key={entry.id}
                              className="flex items-center justify-between text-sm border-b pb-2 last:border-0"
                            >
                              <div>
                                <p className="font-medium">{entry.name || entry.email}</p>
                                {entry.name && (
                                  <p className="text-xs text-muted-foreground">{entry.email}</p>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {formatDateCompact(entry.createdAt)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 pt-2">
                          <Select value={notifySessionId} onValueChange={setNotifySessionId}>
                            <SelectTrigger className="w-auto min-w-[14rem]">
                              <SelectValue placeholder="Choose a session to announce" />
                            </SelectTrigger>
                            <SelectContent>
                              {((sessions as any[]) || [])
                                .filter(
                                  (session: any) => new Date(session.startDateTime) > new Date()
                                )
                                .map((session: any) => (
                                  <SelectItem key={session.id} value={session.id}>
                                    {formatDateWithTimezone(session.startDateTime, {
                                      includeTime: true,
                                    })}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!notifySessionId || notifyWaitlistMutation.isPending}
                            onClick={() => notifyWaitlistMutation.mutate(notifySessionId)}
                          >
                            {notifyWaitlistMutation.isPending ? 'Notifying…' : 'Notify waitlist'}
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Add New Session Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {editingSession ? 'Edit Session' : 'Add New Session'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateSession} className="grid gap-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <DateTimePicker
                            id="startDateTime"
                            label="Start Date & Time"
                            value={newSession.startDateTime}
                            onChange={value => {
                              const newSessionData = { ...newSession, startDateTime: value };

                              // Auto-populate end time only when creating new sessions (not editing)
                              if (!editingSession && value && managingSessionsFor) {
                                const startDate = new Date(value);
                                if (!isNaN(startDate.getTime()) && managingSessionsFor.duration) {
                                  // Use the class duration in minutes to calculate end time
                                  const durationMinutes =
                                    parseInt(managingSessionsFor.duration) || 60; // Default to 60 minutes if no duration
                                  const endDate = new Date(
                                    startDate.getTime() + durationMinutes * 60 * 1000
                                  );
                                  const endDateTimeString = utcToLocalDatetimeString(
                                    endDate.toISOString()
                                  );
                                  newSessionData.endDateTime = endDateTimeString;
                                }
                              }

                              setNewSession(newSessionData);
                            }}
                            required
                            placeholder="Select start date and time"
                          />
                        </div>
                        <div>
                          <DateTimePicker
                            id="endDateTime"
                            label={`End Date & Time${!editingSession && managingSessionsFor ? ' (calculated from class duration)' : ''}`}
                            value={newSession.endDateTime}
                            onChange={value => setNewSession({ ...newSession, endDateTime: value })}
                            required
                            readOnly={!editingSession && managingSessionsFor}
                            className={!editingSession && managingSessionsFor ? 'bg-muted' : ''}
                            placeholder="Select end date and time"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label htmlFor="maxStudents">Max Students</Label>
                          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5"
                              checked={newSession.maxStudents === ''}
                              onChange={e =>
                                setNewSession({
                                  ...newSession,
                                  maxStudents: e.target.checked ? '' : '20',
                                })
                              }
                            />
                            Unlimited
                          </label>
                        </div>
                        <Input
                          id="maxStudents"
                          type="number"
                          min="1"
                          value={newSession.maxStudents}
                          onChange={e =>
                            setNewSession({ ...newSession, maxStudents: e.target.value })
                          }
                          placeholder="e.g., 20"
                          required={newSession.maxStudents !== ''}
                          disabled={newSession.maxStudents === ''}
                          data-testid="input-max-students"
                        />
                        {newSession.maxStudents === '' && (
                          <p className="text-xs text-muted-foreground mt-1">
                            No student limit — open enrollment.
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="sessionNotes">Session Notes (Optional)</Label>
                        <Input
                          id="sessionNotes"
                          value={newSession.notes}
                          onChange={e => setNewSession({ ...newSession, notes: e.target.value })}
                          placeholder="e.g., Bring your own coffee beans"
                          data-testid="input-session-notes"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="disable-post-class-email"
                          title="Skip post-class emails"
                          checked={newSession.disablePostClassEmail}
                          onChange={e =>
                            setNewSession({
                              ...newSession,
                              disablePostClassEmail: e.target.checked,
                            })
                          }
                          className="h-4 w-4"
                        />
                        <Label htmlFor="disable-post-class-email" className="cursor-pointer">
                          Skip post-class emails for this session
                        </Label>
                      </div>

                      {/* Recurrence — only shown when creating (not editing) */}
                      {!editingSession && (
                        <div className="space-y-3 border rounded-md p-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="session-is-recurring"
                              title="Recurring schedule"
                              checked={newSession.isRecurring}
                              onChange={e =>
                                setNewSession(s => ({ ...s, isRecurring: e.target.checked }))
                              }
                              className="h-4 w-4"
                            />
                            <Label
                              htmlFor="session-is-recurring"
                              className="cursor-pointer font-medium"
                            >
                              Recurring schedule
                            </Label>
                          </div>
                          {newSession.isRecurring && (
                            <div className="space-y-3 pt-1">
                              <p className="text-xs text-muted-foreground">
                                Creates this session and automatically generates future ones at the
                                same time. Students enroll per session.
                              </p>
                              <div>
                                <Label>Frequency</Label>
                                <Select
                                  value={newSession.recurrenceFreq}
                                  onValueChange={v =>
                                    setNewSession(s => ({
                                      ...s,
                                      recurrenceFreq: v as RecurrenceFreq,
                                    }))
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="weekly">Weekly</SelectItem>
                                    <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                                    <SelectItem value="monthly">Monthly</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {newSession.recurrenceFreq !== 'monthly' && (
                                <div>
                                  <Label>Days</Label>
                                  <div className="flex gap-1 flex-wrap pt-1">
                                    {WEEKDAY_CODES.map(day => (
                                      <button
                                        key={day}
                                        type="button"
                                        onClick={() =>
                                          setNewSession(s => ({
                                            ...s,
                                            recurrenceDays: s.recurrenceDays.includes(day)
                                              ? s.recurrenceDays.filter(d => d !== day)
                                              : [...s.recurrenceDays, day],
                                          }))
                                        }
                                        className={`px-2 py-1 rounded text-xs border ${
                                          newSession.recurrenceDays.includes(day)
                                            ? 'bg-primary text-primary-foreground border-primary'
                                            : 'bg-background text-foreground border-input'
                                        }`}
                                      >
                                        {WEEKDAY_LABELS[day]}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div>
                                <Label htmlFor="session-recurrence-ends">End date (optional)</Label>
                                <Input
                                  id="session-recurrence-ends"
                                  type="date"
                                  value={newSession.recurrenceEndsAt}
                                  onChange={e =>
                                    setNewSession(s => ({ ...s, recurrenceEndsAt: e.target.value }))
                                  }
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Leave blank to generate sessions on a rolling 8-week window.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          type="submit"
                          disabled={
                            createSessionMutation.isPending || updateSessionMutation.isPending
                          }
                          data-testid="button-add-session"
                        >
                          {createSessionMutation.isPending || updateSessionMutation.isPending
                            ? editingSession
                              ? 'Updating...'
                              : 'Adding...'
                            : editingSession
                              ? 'Update Session'
                              : 'Add Session'}
                        </Button>
                        {editingSession && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setEditingSession(null);
                              setNewSession({
                                ...getDefaultSessionTimes(),
                                maxStudents: '',
                                notes: '',
                                disablePostClassEmail: false,
                                isRecurring: false,
                                recurrenceFreq: 'weekly',
                                recurrenceDays: [],
                                recurrenceEndsAt: '',
                              });
                            }}
                            data-testid="button-cancel-edit-session"
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>

                {/* Existing Sessions List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Scheduled Sessions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {sessionsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                      </div>
                    ) : sessions && Array.isArray(sessions) && sessions.length > 0 ? (
                      <div className="space-y-4">
                        {(() => {
                          const visibleSessions = sessions.filter(
                            (s: any) => !s.isCancelled || s.enrolledCount > 0
                          );
                          if (visibleSessions.length === 0) {
                            return (
                              <p className="text-sm text-muted-foreground text-center py-2">
                                No active sessions.
                              </p>
                            );
                          }
                          return visibleSessions.map((session: any) => (
                            <div
                              key={session.id}
                              className={`border rounded-lg p-4 ${session.isCancelled ? 'opacity-60 bg-muted/40' : ''}`}
                              data-testid={`session-${session.id}`}
                            >
                              {session.isCancelled && (
                                <div className="flex items-center gap-2 mb-3">
                                  <Badge
                                    variant="destructive"
                                    className="rounded-full px-3 py-0.5 text-xs"
                                  >
                                    Cancelled
                                  </Badge>
                                  {session.cancellationNote && (
                                    <span className="text-xs text-muted-foreground">
                                      {session.cancellationNote}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                                <div className="grid gap-2 min-w-0">
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <p className="font-medium">Start Time</p>
                                      <p className="text-muted-foreground">
                                        {formatDateWithTimezone(session.startDateTime, {
                                          includeTime: true,
                                        })}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="font-medium">End Time</p>
                                      <p className="text-muted-foreground">
                                        {formatDateWithTimezone(session.endDateTime, {
                                          includeTime: true,
                                        })}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <p className="font-medium">Max Students</p>
                                      <p className="text-muted-foreground">
                                        {session.maxStudents || session.availableSpots}
                                      </p>
                                    </div>
                                    {session.notes && (
                                      <div>
                                        <p className="font-medium">Notes</p>
                                        <p className="text-muted-foreground">{session.notes}</p>
                                      </div>
                                    )}
                                    {session.disablePostClassEmail && (
                                      <div>
                                        <p className="font-medium">Post-Class Email</p>
                                        <p className="text-muted-foreground">Skipped</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2 flex-wrap shrink-0">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      setViewingRosterFor({
                                        sessionId: session.id,
                                        classId: managingSessionsFor.id,
                                        sessionLabel: formatDateCompact(session.startDateTime),
                                      })
                                    }
                                    data-testid={`button-view-roster-${session.id}`}
                                  >
                                    <Users size={14} className="mr-1" />
                                    Roster
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingSession(session);
                                      setNewSession({
                                        startDateTime: utcToLocalDatetimeString(
                                          session.startDateTime
                                        ),
                                        endDateTime: utcToLocalDatetimeString(session.endDateTime),
                                        maxStudents: session.maxStudents?.toString() || '',
                                        notes: session.notes || '',
                                        disablePostClassEmail:
                                          session.disablePostClassEmail ?? false,
                                        isRecurring: false,
                                        recurrenceFreq: 'weekly',
                                        recurrenceDays: [],
                                        recurrenceEndsAt: '',
                                      });
                                      // Scroll to form
                                      setTimeout(() => {
                                        document
                                          .querySelector('[data-testid="input-start-datetime"]')
                                          ?.scrollIntoView({ behavior: 'smooth' });
                                      }, 100);
                                    }}
                                    data-testid={`button-edit-session-${session.id}`}
                                  >
                                    <Edit size={14} className="mr-1" />
                                    Edit
                                  </Button>
                                  {!session.isCancelled ? (
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => {
                                        setCancelScope('single');
                                        setCancelNote('');
                                        setCancelingSession({
                                          id: session.id,
                                          startDateTime: session.startDateTime,
                                        });
                                      }}
                                      data-testid={`button-cancel-session-${session.id}`}
                                    >
                                      <Trash2 size={14} className="mr-1" />
                                      Cancel
                                    </Button>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs">
                                      Cancelled
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    ) : (
                      <div className="text-center py-8" data-testid="no-sessions">
                        <p className="text-muted-foreground">No sessions scheduled yet.</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Add your first session above.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setManagingSessionsFor(null)}
                  data-testid="button-close-sessions"
                >
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Session Cancel Dialog */}
          <Dialog
            open={!!cancelingSession}
            onOpenChange={open => {
              if (!open) {
                setCancelingSession(null);
                setCancelFeeBearer('creator');
              }
            }}
          >
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Cancel Session</DialogTitle>
                <DialogDescription>Choose how many sessions to cancel.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2">
                  {(['single', 'forward'] as const).map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setCancelScope(s)}
                      className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                        cancelScope === s
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-input'
                      }`}
                    >
                      {s === 'single' ? 'This session only' : 'This + all future'}
                    </button>
                  ))}
                </div>
                <div>
                  <Label htmlFor="cancel-note">Note (optional)</Label>
                  <Input
                    id="cancel-note"
                    value={cancelNote}
                    onChange={e => setCancelNote(e.target.value)}
                    placeholder="Reason for cancellation"
                  />
                </div>
                {cancelClassIsFree ? (
                  <p className="text-xs text-muted-foreground">
                    This is a free class — there&rsquo;s no payment or processing fee. Attendees
                    will simply have their booking cancelled.
                  </p>
                ) : (
                  <div>
                    <Label>Card processing fee</Label>
                    <p className="mb-2 text-xs text-muted-foreground">
                      Stripe keeps the ~3% card fee even on refunds. Choose who pays it.
                    </p>
                    <div className="flex gap-2">
                      {(
                        [
                          { value: 'creator', label: 'I pay the fee' },
                          { value: 'customer', label: 'Customer pays the fee' },
                        ] as const
                      ).map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setCancelFeeBearer(opt.value)}
                          className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                            cancelFeeBearer === opt.value
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background border-input'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {cancelFeeBearer === 'creator'
                        ? 'Attendees get a full refund; the processing fee comes out of your earnings.'
                        : 'Attendees are refunded their payment minus the processing fee.'}
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCancelingSession(null)}>
                  Back
                </Button>
                <Button
                  variant="destructive"
                  disabled={cancelSessionMutation.isPending}
                  onClick={() => {
                    if (!cancelingSession) return;
                    cancelSessionMutation.mutate({
                      sessionId: cancelingSession.id,
                      scope: cancelScope,
                      note: cancelNote || undefined,
                      feeBearer: cancelFeeBearer,
                    });
                  }}
                >
                  {cancelSessionMutation.isPending ? 'Cancelling…' : 'Confirm Cancel'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Session Roster Dialog */}
          <Dialog open={!!viewingRosterFor} onOpenChange={() => setViewingRosterFor(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Attendee Roster — {viewingRosterFor?.sessionLabel}</DialogTitle>
                <DialogDescription>
                  {rosterData && Array.isArray((rosterData as any).data)
                    ? `${(rosterData as any).data.length} ${(rosterData as any).data.length === 1 ? 'attendee' : 'attendees'} for this session.`
                    : 'All enrolled attendees for this session.'}
                </DialogDescription>
              </DialogHeader>
              {rosterLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : rosterData &&
                Array.isArray((rosterData as any).data) &&
                (rosterData as any).data.length > 0 ? (
                <div className="space-y-2 max-h-[50dvh] overflow-y-auto">
                  {(rosterData as any).data.map((attendee: any, idx: number) => {
                    const waiver = attendee.waiver;
                    const waiverStatus = waiver?.status || 'not_required';
                    const canRemind =
                      !attendee.isPlaceholder &&
                      waiver?.required &&
                      (waiverStatus === 'pending' || waiverStatus === 'missing');
                    const recipientEmail = waiverRecipientEmails[attendee.id] || '';
                    return (
                      <div
                        key={attendee.id}
                        className="grid gap-3 border rounded-md px-3 py-2 text-sm sm:grid-cols-[1fr_auto]"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">
                              {attendee.displayName || attendee.guestName || `Attendee ${idx + 1}`}
                            </span>
                            {attendee.isPurchaser && (
                              <Badge variant="secondary" className="text-xs">
                                Purchaser
                              </Badge>
                            )}
                            {attendee.bookingType === 'punch_pass' && (
                              <Badge variant="outline" className="text-xs">
                                Punch pass
                              </Badge>
                            )}
                            {attendee.ticketNumber && (
                              <Badge variant="outline" className="text-xs font-mono">
                                #{attendee.ticketNumber}
                              </Badge>
                            )}
                          </div>
                          {attendee.isPlaceholder && (
                            <p className="mt-1 text-xs text-amber-700">
                              Seat included in this booking&rsquo;s ticket count, but no name or
                              contact info was recorded for it.
                            </p>
                          )}
                          {attendee.orderTicketCount > 1 && (
                            <p className="mt-1 text-xs text-muted-foreground">
                              Part of a {attendee.orderTicketCount}-ticket booking
                              {typeof attendee.orderNetAmountCents === 'number'
                                ? ` • $${(attendee.orderNetAmountCents / 100).toFixed(2)} total`
                                : ''}
                            </p>
                          )}
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            {attendee.email && (
                              <span className="inline-flex items-center gap-1">
                                <Mail size={12} />
                                {attendee.email}
                              </span>
                            )}
                            {attendee.phone && (
                              <span className="inline-flex items-center gap-1">
                                <Phone size={12} />
                                {attendee.phone}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            {waiverStatus === 'signed' || waiverStatus === 'signed_on_file' ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : waiver?.required ? (
                              <Circle className="h-4 w-4 text-amber-600" />
                            ) : (
                              <Circle className="h-4 w-4" />
                            )}
                            <span>
                              {waiverStatus === 'signed'
                                ? 'Waiver signed'
                                : waiverStatus === 'signed_on_file'
                                  ? 'Waiver signed on file'
                                  : waiverStatus === 'pending'
                                    ? 'Waiver pending'
                                    : waiverStatus === 'missing'
                                      ? 'Waiver not sent'
                                      : 'No waiver required'}
                            </span>
                            {waiver?.signedAt && (
                              <span>- {new Date(waiver.signedAt).toLocaleDateString()}</span>
                            )}
                            {waiver?.signerEmail && <span>- {waiver.signerEmail}</span>}
                          </div>
                          {(waiverStatus === 'signed' || waiverStatus === 'signed_on_file') &&
                            waiver?.mediaReleaseConsent != null && (
                              <div className="mt-1 text-xs">
                                <span
                                  className={
                                    waiver.mediaReleaseConsent ? 'text-green-700' : 'text-amber-700'
                                  }
                                >
                                  Photo release: {waiver.mediaReleaseConsent ? 'Yes' : 'No'}
                                </span>
                              </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-2 sm:w-56">
                          {canRemind && (
                            <>
                              <Input
                                type="email"
                                value={recipientEmail}
                                onChange={event =>
                                  setWaiverRecipientEmails(current => ({
                                    ...current,
                                    [attendee.id]: event.target.value,
                                  }))
                                }
                                placeholder="Guest email optional"
                                className="h-8 text-xs"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={waiverReminderMutation.isPending}
                                onClick={() =>
                                  waiverReminderMutation.mutate({
                                    classId: viewingRosterFor!.classId,
                                    sessionId: viewingRosterFor!.sessionId,
                                    attendeeId: attendee.id,
                                    recipientEmail: recipientEmail.trim() || undefined,
                                  })
                                }
                              >
                                <Mail size={14} className="mr-1" />
                                {waiverStatus === 'missing' ? 'Send Waiver' : 'Remind'}
                              </Button>
                            </>
                          )}
                          {attendee.orderId &&
                            (cancelingTicketOrderId === attendee.orderId ? (
                              <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-2">
                                {(attendee.orderNetAmountCents ?? 0) <= 0 ? (
                                  <>
                                    <p className="text-xs font-medium text-amber-700">
                                      Cancel this free booking? There&rsquo;s no payment or fee to
                                      refund.
                                    </p>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="w-full"
                                      disabled={cancelTicketMutation.isPending}
                                      onClick={() =>
                                        cancelTicketMutation.mutate({
                                          orderId: attendee.orderId,
                                          feeBearer: 'creator',
                                        })
                                      }
                                    >
                                      Cancel booking
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-xs font-medium text-amber-700">
                                      Cancel & refund this ticket? Who pays the processing fee?
                                    </p>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="w-full"
                                      disabled={cancelTicketMutation.isPending}
                                      onClick={() =>
                                        cancelTicketMutation.mutate({
                                          orderId: attendee.orderId,
                                          feeBearer: 'creator',
                                        })
                                      }
                                      title="Full refund; you pay the processing fee"
                                    >
                                      Refund (you pay the fee)
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full"
                                      disabled={cancelTicketMutation.isPending}
                                      onClick={() =>
                                        cancelTicketMutation.mutate({
                                          orderId: attendee.orderId,
                                          feeBearer: 'customer',
                                        })
                                      }
                                      title="Refund minus the processing fee (student pays it)"
                                    >
                                      Refund (customer pays the fee)
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="w-full"
                                  onClick={() => setCancelingTicketOrderId(null)}
                                >
                                  Keep
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setCancelingTicketOrderId(attendee.orderId)}
                              >
                                Cancel ticket
                              </Button>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center py-6 text-muted-foreground text-sm">
                  No attendees enrolled yet.
                </p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewingRosterFor(null)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            open={!!unpublishConfirm}
            onOpenChange={open => !open && setUnpublishConfirm(null)}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Unpublish {unpublishConfirm?.title}?</DialogTitle>
                <DialogDescription>
                  This class has {unpublishConfirm?.sessionCount}{' '}
                  {unpublishConfirm?.sessionCount === 1 ? 'session' : 'sessions'} scheduled.
                  Unpublishing takes the class off the site, but scheduled sessions stay on your
                  calendar unless you cancel them too. Cancelling refunds any paid attendees.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-2">
                <Button
                  variant="destructive"
                  disabled={unpublishAndCancelSessionsMutation.isPending}
                  onClick={() =>
                    unpublishConfirm &&
                    unpublishAndCancelSessionsMutation.mutate({
                      classId: unpublishConfirm.classId,
                      anchorSessionId: unpublishConfirm.anchorSessionId,
                    })
                  }
                  data-testid="button-unpublish-and-cancel-sessions"
                >
                  {unpublishAndCancelSessionsMutation.isPending
                    ? 'Cancelling sessions…'
                    : `Unpublish & cancel ${unpublishConfirm?.sessionCount === 1 ? 'the session' : 'all sessions'}`}
                </Button>
                <Button
                  variant="outline"
                  disabled={togglePublishMutation.isPending}
                  onClick={() => {
                    if (!unpublishConfirm) return;
                    togglePublishMutation.mutate({
                      classId: unpublishConfirm.classId,
                      publish: false,
                    });
                    setUnpublishConfirm(null);
                  }}
                  data-testid="button-unpublish-keep-sessions"
                >
                  Just unpublish (keep sessions scheduled)
                </Button>
                <Button variant="ghost" onClick={() => setUnpublishConfirm(null)}>
                  Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stripe Connect banner — only when payouts are actually needed (a paid
            class exists, or no classes yet) and not yet complete. */}
        {!onboardingDataLoading &&
          connectStatus &&
          shouldIncludePayoutOnboardingItem &&
          !payoutOnboardingComplete && (
            <div className="flex items-center justify-between gap-4 rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-700 px-4 py-3 text-sm">
              <div className="flex items-center gap-2">
                <DollarSign size={16} className="text-orange-600 shrink-0" />
                <span className="text-orange-800 dark:text-orange-300">
                  {connectStatus.onboardingStatus === 'in_progress'
                    ? 'Finish setting up your payout account to create paid classes.'
                    : 'Set up payouts to start creating paid classes.'}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 border-orange-400 text-orange-700 hover:bg-orange-100 dark:text-orange-300"
                onClick={() => connectOnboardMutation.mutate()}
                disabled={connectOnboardMutation.isPending}
              >
                {connectOnboardMutation.isPending ? 'Redirecting…' : 'Set up payouts'}
              </Button>
            </div>
          )}

        {!onboardingDataLoading && incompleteOnboardingItems.length > 0 && (
          <Card data-testid="creator-onboarding-checklist">
            <CardHeader>
              <CardTitle>Creator Setup</CardTitle>
              <CardDescription>
                Finish these steps before sending students to your first published class.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {onboardingItems.map(item => (
                <div
                  key={item.label}
                  className="flex flex-col gap-3 rounded-lg border border-border/70 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex gap-3">
                    {item.complete ? (
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                    ) : (
                      <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium text-foreground">{item.label}</p>
                      <p className="text-sm text-muted-foreground">{item.helper}</p>
                    </div>
                  </div>
                  {!item.complete && item.action && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={item.action}
                      disabled={connectOnboardMutation.isPending}
                      className="shrink-0"
                    >
                      {item.actionLabel}
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="profile" className="space-y-6">
          <div className="overflow-x-auto overscroll-x-contain">
            <TabsList className="inline-flex w-max">
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <Users size={16} />
                Profile
              </TabsTrigger>
              <TabsTrigger value="classes" className="flex items-center gap-2">
                <BookOpen size={16} />
                My Classes
              </TabsTrigger>
              <TabsTrigger value="students" className="flex items-center gap-2">
                <Users size={16} />
                Students
              </TabsTrigger>
              <TabsTrigger value="passes" className="flex items-center gap-2">
                <Ticket size={16} />
                Punch Passes
              </TabsTrigger>
              <TabsTrigger value="schedule" className="flex items-center gap-2">
                <Calendar size={16} />
                Schedule
              </TabsTrigger>
              <TabsTrigger value="archive" className="flex items-center gap-2">
                <Archive size={16} />
                Archive
              </TabsTrigger>
              <TabsTrigger value="refunds" className="flex items-center gap-2">
                <DollarSign size={16} />
                Refunds
              </TabsTrigger>
              <TabsTrigger value="earnings" className="flex items-center gap-2">
                <TrendingUp size={16} />
                Earnings
              </TabsTrigger>
              <TabsTrigger value="broadcast" className="flex items-center gap-2">
                <Mail size={16} />
                Broadcast
              </TabsTrigger>
              {user?.isBlogWriter && (
                <TabsTrigger value="writing" className="flex items-center gap-2">
                  <PenLine size={16} />
                  Writing
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="profile">
            <Card data-testid="profile-card">
              <CardHeader>
                <CardTitle>Creator Profile</CardTitle>
                <CardDescription>Manage your public creator profile information</CardDescription>
              </CardHeader>
              <CardContent>
                {creator && typeof creator === 'object' && !isEditingProfile ? (
                  <div className="space-y-6">
                    <div className="flex flex-col gap-4 rounded-lg border border-border/70 p-4 sm:flex-row sm:items-center">
                      {creatorProfileImageUrl ? (
                        <img
                          src={creatorProfileImageUrl}
                          alt={(creator as any)?.displayName || 'Creator profile'}
                          className="h-24 w-24 rounded-full object-cover ring-1 ring-border/70"
                          data-testid="creator-profile-photo"
                          onError={() => setProfileImageFailed(true)}
                        />
                      ) : (
                        <div
                          className="flex h-24 w-24 items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border/70"
                          data-testid="creator-profile-photo-placeholder"
                        >
                          <UserRound className="h-12 w-12" />
                        </div>
                      )}
                      <div className="space-y-2">
                        <h3 className="font-semibold">Profile Photo</h3>
                        <p className="max-w-xl text-sm text-muted-foreground">
                          This photo appears on your public creator profile, class cards, and
                          instructor sections.
                        </p>
                        <div>
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            disabled={uploadingProfilePicture}
                            data-testid="button-upload-creator-photo"
                          >
                            <label
                              htmlFor="creator-profile-photo-upload"
                              className="cursor-pointer"
                            >
                              <Camera className="mr-2 h-4 w-4" />
                              {uploadingProfilePicture ? 'Uploading...' : 'Upload Photo'}
                            </label>
                          </Button>
                          <input
                            id="creator-profile-photo-upload"
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            disabled={uploadingProfilePicture}
                            onChange={event => {
                              const file = event.target.files?.[0] ?? null;
                              void handleProfilePictureUpload(file);
                              event.target.value = '';
                            }}
                            data-testid="input-upload-creator-photo"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold mb-2">Display Name</h3>
                        <p className="text-muted-foreground" data-testid="profile-display-name">
                          {(creator as any)?.displayName || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Creator Headline</h3>
                        <p className="text-muted-foreground" data-testid="profile-specialty">
                          {(creator as any)?.creatorHeadline || 'Not set'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Bio</h3>
                      <p className="text-muted-foreground" data-testid="profile-bio">
                        {(creator as any)?.bio || 'No bio provided'}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Experience</h3>
                      <p className="text-muted-foreground" data-testid="profile-experience">
                        {(creator as any)?.experience || 'No experience details provided'}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Website URL</h3>
                      <p className="text-muted-foreground" data-testid="profile-portfolio">
                        {(creator as any)?.portfolioUrl || 'Not set'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          const creatorData = creator as any;
                          setProfileForm({
                            displayName: creatorData?.displayName || '',
                            bio: creatorData?.bio || '',
                            creatorHeadline: creatorData?.creatorHeadline || '',
                            experience: creatorData?.experience || '',
                            portfolioUrl: creatorData?.portfolioUrl || '',
                            socialLinks: {
                              instagram: creatorData?.socialLinks?.instagram || '',
                              twitter: creatorData?.socialLinks?.twitter || '',
                            },
                          });
                          setIsEditingProfile(true);
                        }}
                        data-testid="button-edit-profile"
                      >
                        Edit Profile
                      </Button>
                    </div>
                  </div>
                ) : isEditingProfile ? (
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div>
                      <Label htmlFor="displayName">Display Name</Label>
                      <Input
                        id="displayName"
                        value={profileForm.displayName}
                        onChange={e =>
                          setProfileForm({ ...profileForm, displayName: e.target.value })
                        }
                        data-testid="input-display-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="creatorHeadline">Creator Headline</Label>
                      <Input
                        id="creatorHeadline"
                        value={profileForm.creatorHeadline}
                        onChange={e =>
                          setProfileForm({ ...profileForm, creatorHeadline: e.target.value })
                        }
                        placeholder="e.g., Coffee Education, Photography"
                        data-testid="input-specialty"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        value={profileForm.bio}
                        onChange={e => setProfileForm({ ...profileForm, bio: e.target.value })}
                        placeholder="Tell people about yourself and your expertise"
                        rows={6}
                        data-testid="input-bio"
                      />
                    </div>
                    <div>
                      <Label htmlFor="experience">Experience</Label>
                      <Textarea
                        id="experience"
                        value={profileForm.experience}
                        onChange={e =>
                          setProfileForm({ ...profileForm, experience: e.target.value })
                        }
                        placeholder="Describe your relevant experience and qualifications"
                        data-testid="input-experience"
                      />
                    </div>
                    <div>
                      <Label htmlFor="portfolioUrl">Website URL</Label>
                      <Input
                        id="portfolioUrl"
                        type="url"
                        value={profileForm.portfolioUrl}
                        onChange={e =>
                          setProfileForm({ ...profileForm, portfolioUrl: e.target.value })
                        }
                        onBlur={handlePortfolioBlur}
                        placeholder="https://your-website.com"
                        data-testid="input-portfolio-url"
                      />
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Social Media Links</h3>
                      <div>
                        <Label htmlFor="instagram">Instagram URL</Label>
                        <Input
                          id="instagram"
                          type="url"
                          value={profileForm.socialLinks.instagram}
                          onChange={e =>
                            setProfileForm({
                              ...profileForm,
                              socialLinks: {
                                ...profileForm.socialLinks,
                                instagram: e.target.value,
                              },
                            })
                          }
                          onBlur={() => {
                            if (
                              profileForm.socialLinks.instagram &&
                              !profileForm.socialLinks.instagram.match(/^https?:\/\//)
                            ) {
                              setProfileForm({
                                ...profileForm,
                                socialLinks: {
                                  ...profileForm.socialLinks,
                                  instagram: `https://${profileForm.socialLinks.instagram}`,
                                },
                              });
                            }
                          }}
                          placeholder="https://instagram.com/username"
                        />
                      </div>
                      <div>
                        <Label htmlFor="twitter">Twitter URL</Label>
                        <Input
                          id="twitter"
                          type="url"
                          value={profileForm.socialLinks.twitter}
                          onChange={e =>
                            setProfileForm({
                              ...profileForm,
                              socialLinks: { ...profileForm.socialLinks, twitter: e.target.value },
                            })
                          }
                          onBlur={() => {
                            if (
                              profileForm.socialLinks.twitter &&
                              !profileForm.socialLinks.twitter.match(/^https?:\/\//)
                            ) {
                              setProfileForm({
                                ...profileForm,
                                socialLinks: {
                                  ...profileForm.socialLinks,
                                  twitter: `https://${profileForm.socialLinks.twitter}`,
                                },
                              });
                            }
                          }}
                          placeholder="https://twitter.com/username"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        disabled={updateProfileMutation.isPending}
                        data-testid="button-save-profile"
                      >
                        {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsEditingProfile(false)}
                        data-testid="button-cancel-edit"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Loading profile...</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Calendar feed / ICS token */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Calendar Feed</CardTitle>
                <CardDescription>
                  Subscribe to your sessions in any calendar app (Apple Calendar, Google Calendar,
                  Outlook) using this private link.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {webcalLink ? (
                  <>
                    <div className="flex flex-col gap-2">
                      <code className="block rounded-lg border border-border/70 bg-muted/60 px-3 py-2 text-xs break-all">
                        {webcalLink}
                      </code>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          void navigator.clipboard.writeText(webcalLink);
                          toast({ title: 'Link copied to clipboard' });
                        }}
                      >
                        Copy link
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Rotating the link will invalidate your current calendar subscriptions. Continue?'
                            )
                          ) {
                            rotateIcsTokenMutation.mutate();
                          }
                        }}
                        disabled={rotateIcsTokenMutation.isPending}
                      >
                        {rotateIcsTokenMutation.isPending ? 'Rotating…' : 'Rotate link'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Treat this link like a password — it grants read-only access to your session
                      schedule. Rotate it if you ever share it accidentally.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Loading calendar link…</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="classes">
            <div className="grid gap-4">
              {classesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : classes && Array.isArray(classes) && classes.length > 0 ? (
                [...classes]
                  .sort((a: any, b: any) => {
                    // Order: published first, then unpublished drafts, archived last.
                    if (!!a.isArchived !== !!b.isArchived) return a.isArchived ? 1 : -1;
                    if (!!a.isPublished !== !!b.isPublished) return a.isPublished ? -1 : 1;
                    // Within the same group, maintain original order
                    return 0;
                  })
                  .map((classItem: any) => (
                    <Card key={classItem.id} data-testid={`class-card-${classItem.id}`}>
                      <CardHeader>
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <img
                              src={normalizeImageUrl(
                                classItem.thumbnailUrl || '',
                                PLACEHOLDER.thumb
                              )}
                              alt={classItem.title}
                              onError={imageErrorFallback(PLACEHOLDER.thumb, classItem.title)}
                              className="h-12 w-12 rounded-lg object-cover shrink-0"
                            />
                            <div className="min-w-0">
                              <CardTitle data-testid={`class-title-${classItem.id}`}>
                                {classItem.title}
                              </CardTitle>
                              {classItem.description && (
                                <>
                                  <CardDescription
                                    data-testid={`class-description-${classItem.id}`}
                                    className={
                                      expandedClassDescriptions.has(classItem.id)
                                        ? 'whitespace-pre-wrap'
                                        : 'line-clamp-2 whitespace-pre-wrap'
                                    }
                                  >
                                    {classItem.description}
                                  </CardDescription>
                                  {classItem.description.length > 100 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedClassDescriptions(prev => {
                                          const next = new Set(prev);
                                          if (next.has(classItem.id)) {
                                            next.delete(classItem.id);
                                          } else {
                                            next.add(classItem.id);
                                          }
                                          return next;
                                        })
                                      }
                                      className="mt-0.5 text-xs font-medium text-primary hover:underline"
                                      data-testid={`toggle-description-${classItem.id}`}
                                    >
                                      {expandedClassDescriptions.has(classItem.id)
                                        ? 'Show less'
                                        : 'Show more'}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Badge variant="outline" data-testid={`class-price-${classItem.id}`}>
                              ${classItem.price}
                            </Badge>
                            {classItem.isArchived ? (
                              <Badge
                                variant="secondary"
                                data-testid={`class-archived-${classItem.id}`}
                              >
                                Archived
                              </Badge>
                            ) : classItem.isPublished ? (
                              <Badge
                                className="bg-green-100 text-green-800 border-green-200"
                                data-testid={`class-published-${classItem.id}`}
                              >
                                Published
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-muted-foreground"
                                data-testid={`class-draft-${classItem.id}`}
                              >
                                Draft
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 text-sm mb-4">
                          <div>
                            <p className="font-medium">Category</p>
                            <p
                              className="text-muted-foreground"
                              data-testid={`class-category-${classItem.id}`}
                            >
                              {classItem.category}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium">Duration</p>
                            <p
                              className="text-muted-foreground"
                              data-testid={`class-duration-${classItem.id}`}
                            >
                              {classItem.duration} minutes
                            </p>
                          </div>
                          <div>
                            <p className="font-medium">Sessions</p>
                            <p
                              className="text-muted-foreground"
                              data-testid={`class-sessions-${classItem.id}`}
                            >
                              {classItem.sessions?.length || 0} scheduled
                            </p>
                          </div>
                          <div>
                            <p className="font-medium">Earned</p>
                            <p className="text-muted-foreground">
                              ${((classTotalsMap[classItem.id] ?? 0) / 100).toFixed(2)}
                            </p>
                          </div>
                        </div>

                        {/* Sessions list */}
                        {classItem.sessions && classItem.sessions.length > 0 && (
                          <div className="mb-4">
                            <p className="font-medium text-sm mb-2">Scheduled Sessions:</p>
                            <div className="space-y-2">
                              {(expandedClassSessions.has(classItem.id)
                                ? classItem.sessions
                                : classItem.sessions.slice(0, 1)
                              ).map((session: any) => {
                                const remainingSpots = getSessionRemainingSpots(session);
                                const full = isSessionFull(session);
                                return (
                                  <div
                                    key={session.id}
                                    className="bg-muted p-3 rounded-md text-sm"
                                    data-testid={`session-${session.id}`}
                                  >
                                    <div className="flex justify-between items-start gap-3">
                                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4 flex-1 min-w-0">
                                        <div className="min-w-0">
                                          <p className="font-medium text-primary">Date & Time</p>
                                          <p className="text-muted-foreground break-words">
                                            {formatDateTimeRange(
                                              session.startDateTime,
                                              session.endDateTime
                                            )}
                                          </p>
                                        </div>
                                        <div className="min-w-0">
                                          <p className="font-medium text-primary">Enrollment</p>
                                          <p className="text-muted-foreground break-words">
                                            <span className="font-medium">
                                              {session.enrolledCount}
                                            </span>{' '}
                                            enrolled /{' '}
                                            <span className="font-medium">
                                              {session.maxStudents || session.availableSpots}
                                            </span>{' '}
                                            total /{' '}
                                            <span className="font-medium">
                                              {remainingSpots ?? 'Open'}
                                            </span>{' '}
                                            {remainingSpots == null ? 'availability' : 'remaining'}
                                          </p>
                                        </div>
                                        <div className="min-w-0">
                                          <p className="font-medium text-primary">Earned</p>
                                          <p className="text-muted-foreground">
                                            $
                                            {((sessionTotalsMap[session.id] ?? 0) / 100).toFixed(2)}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="shrink-0 flex flex-col items-end gap-2">
                                        <Badge variant={full ? 'destructive' : 'outline'}>
                                          {full ? 'Full' : 'Available'}
                                        </Badge>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 px-2 text-xs"
                                          onClick={() =>
                                            setViewingRosterFor({
                                              sessionId: session.id,
                                              classId: classItem.id,
                                              sessionLabel: formatDateCompact(
                                                session.startDateTime
                                              ),
                                            })
                                          }
                                          data-testid={`button-class-list-roster-${session.id}`}
                                        >
                                          <Users size={12} className="mr-1" />
                                          Roster ({session.enrolledCount})
                                        </Button>
                                      </div>
                                    </div>
                                    {session.notes && (
                                      <div className="mt-2 pt-2 border-t border-border">
                                        <p className="font-medium text-primary">Notes:</p>
                                        <p className="text-muted-foreground">{session.notes}</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {classItem.sessions.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="mt-2 h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                                onClick={() =>
                                  setExpandedClassSessions(prev => {
                                    const next = new Set(prev);
                                    if (next.has(classItem.id)) {
                                      next.delete(classItem.id);
                                    } else {
                                      next.add(classItem.id);
                                    }
                                    return next;
                                  })
                                }
                                data-testid={`toggle-sessions-${classItem.id}`}
                              >
                                {expandedClassSessions.has(classItem.id) ? (
                                  <>
                                    <ChevronUp size={14} className="mr-1" />
                                    Hide {classItem.sessions.length - 1} more session
                                    {classItem.sessions.length - 1 > 1 ? 's' : ''}
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown size={14} className="mr-1" />
                                    Show {classItem.sessions.length - 1} more session
                                    {classItem.sessions.length - 1 > 1 ? 's' : ''}
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        )}

                        {(!classItem.sessions || classItem.sessions.length === 0) && (
                          <div className="mb-4 p-3 bg-muted rounded-md text-center">
                            <p className="text-muted-foreground text-sm">
                              No sessions scheduled yet.
                            </p>
                            <p className="text-muted-foreground text-xs mt-1">
                              Use the "Sessions" button to add class sessions.
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2 justify-end flex-wrap">
                          {!classItem.isArchived && (
                            <Button
                              size="sm"
                              variant={classItem.isPublished ? 'outline' : 'default'}
                              disabled={togglePublishMutation.isPending}
                              onClick={() => {
                                if (!classItem.isPublished) {
                                  togglePublishMutation.mutate({
                                    classId: classItem.id,
                                    publish: true,
                                  });
                                  return;
                                }
                                const upcomingSessions = Array.isArray(classItem.sessions)
                                  ? classItem.sessions
                                  : [];
                                if (upcomingSessions.length > 0) {
                                  setUnpublishConfirm({
                                    classId: classItem.id,
                                    title: classItem.title,
                                    anchorSessionId: upcomingSessions[0].id,
                                    sessionCount: upcomingSessions.length,
                                  });
                                } else {
                                  togglePublishMutation.mutate({
                                    classId: classItem.id,
                                    publish: false,
                                  });
                                }
                              }}
                              data-testid={`button-publish-class-${classItem.id}`}
                            >
                              {classItem.isPublished ? 'Unpublish' : 'Publish'}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingClass(classItem);
                              // Parse existing RRULE back into form state
                              const existingRule: string | null = classItem.recurrenceRule ?? null;
                              let isRecurring = false;
                              let recurrenceFreq: RecurrenceFreq = 'weekly';
                              let recurrenceDays: string[] = [];
                              if (existingRule) {
                                isRecurring = true;
                                if (existingRule.includes('INTERVAL=2'))
                                  recurrenceFreq = 'biweekly';
                                else if (existingRule.startsWith('FREQ=MONTHLY'))
                                  recurrenceFreq = 'monthly';
                                const dayMatch = existingRule.match(/BYDAY=([A-Z,]+)/);
                                if (dayMatch) recurrenceDays = dayMatch[1].split(',');
                              }
                              setNewClass({
                                title: classItem.title,
                                description: classItem.description,
                                price: classItem.price.toString(),
                                categoryId: classItem.categoryId || '',
                                duration: classItem.duration?.toString() || '',
                                cancellationPolicy:
                                  classItem.cancellationPolicy || DEFAULT_CANCELLATION_POLICY,
                                registrationCutoff: registrationCutoffToFormValue(
                                  classItem.registrationCutoffHours
                                ),
                                registrationCutoffOnlyIfEmpty:
                                  classItem.registrationCutoffOnlyIfEmpty ?? false,
                                deliveryMode:
                                  (classItem.deliveryMode as DeliveryMode | null) || 'in_person',
                                localityId: classItem.localityId || '',
                                pricingMode: classItem.pricingMode || 'standard',
                                suggestedPrice: classItem.suggestedPrice?.toString() || '',
                                promoEnabled: classItem.promoEnabled ?? false,
                                promoPrice:
                                  classItem.promoPrice != null
                                    ? classItem.promoPrice.toString()
                                    : '',
                                promoStartsAt: classItem.promoStartsAt
                                  ? utcToLocalDatetimeString(classItem.promoStartsAt)
                                  : '',
                                promoEndsAt: classItem.promoEndsAt
                                  ? utcToLocalDatetimeString(classItem.promoEndsAt)
                                  : '',
                                isRecurring,
                                recurrenceFreq,
                                recurrenceDays,
                                recurrenceStartTime:
                                  (classItem as { recurrenceStartTime?: string | null })
                                    .recurrenceStartTime ?? '',
                                recurrenceEndsAt: classItem.recurrenceEndsAt
                                  ? new Date(classItem.recurrenceEndsAt).toISOString().slice(0, 10)
                                  : '',
                                passEnabled: classItem.passEnabled ?? false,
                                passSessionCount: classItem.passSessionCount?.toString() ?? '',
                                passPrice: classItem.passPrice
                                  ? (classItem.passPrice / 100).toFixed(2)
                                  : '',
                                passExpiryDays: classItem.passExpiryDays?.toString() ?? '',
                                venueId: (classItem as { venueId?: string | null }).venueId ?? '',
                                livestreamClassId:
                                  (classItem as { livestreamClassId?: string | null })
                                    .livestreamClassId ?? '',
                                feedbackEmailEnabled: classItem.feedbackEmailEnabled ?? false,
                                requiresWaiver: classItem.requiresWaiver ?? false,
                                waiverTitle: classItem.waiverTitle || 'Liability Waiver',
                                waiverText: classItem.waiverText || '',
                                waiverVersion: (classItem.waiverVersion ?? 1).toString(),
                              });
                              setClassThumbnailFile(null);
                              replaceClassThumbnailPreview(classItem.thumbnailUrl || '');
                              setIsAddingClass(true);
                            }}
                            data-testid={`button-edit-class-${classItem.id}`}
                          >
                            <Edit size={14} className="mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setManagingSessionsFor(classItem);
                              // In-person/hybrid classes default to a capped size;
                              // online classes default to unlimited.
                              setNewSession(s => ({
                                ...s,
                                maxStudents: classItem.deliveryMode === 'online' ? '' : '20',
                              }));
                            }}
                            data-testid={`button-sessions-class-${classItem.id}`}
                          >
                            <Calendar size={14} className="mr-1" />
                            Sessions
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setManagingSessionsFor(classItem);
                              setNewSession(s => ({
                                ...s,
                                maxStudents: classItem.deliveryMode === 'online' ? '' : '20',
                              }));
                            }}
                            data-testid={`button-waitlist-class-${classItem.id}`}
                          >
                            <Bell size={14} className="mr-1" />
                            Waitlist
                            {!!waitlistCounts?.counts[classItem.id] && (
                              <Badge variant="secondary" className="ml-1.5 px-1.5">
                                {waitlistCounts.counts[classItem.id]}
                              </Badge>
                            )}
                          </Button>
                          {classItem.isArchived ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (
                                  confirm(
                                    `Are you sure you want to unarchive "${classItem.title}"? This will make it visible on the website again.`
                                  )
                                ) {
                                  unarchiveClassMutation.mutate(classItem.id);
                                }
                              }}
                              data-testid={`button-unarchive-class-${classItem.id}`}
                            >
                              <ArchiveRestore size={14} className="mr-1" />
                              Unarchive
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                if (
                                  confirm(
                                    `Are you sure you want to archive "${classItem.title}"? This will hide it from public view but preserve all data.`
                                  )
                                ) {
                                  archiveClassMutation.mutate(classItem.id);
                                }
                              }}
                              data-testid={`button-archive-class-${classItem.id}`}
                            >
                              <Archive size={14} className="mr-1" />
                              Archive
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              if (
                                confirm(
                                  `Are you sure you want to delete "${classItem.title}"? This action cannot be undone.`
                                )
                              ) {
                                deleteClassMutation.mutate(classItem.id);
                              }
                            }}
                            data-testid={`button-delete-class-${classItem.id}`}
                          >
                            <Trash2 size={14} className="mr-1" />
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
              ) : (
                <Card>
                  <CardContent className="text-center py-8" data-testid="no-classes">
                    <p className="text-muted-foreground">You haven't created any classes yet.</p>
                    <Button
                      onClick={() => setIsAddingClass(true)}
                      className="mt-4"
                      data-testid="button-create-first-class"
                    >
                      Create Your First Class
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="students">
            <Card data-testid="students-card">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle>Enrolled Students</CardTitle>
                    <CardDescription>
                      View contact information, waiver history, and walk-in students
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddingManualStudent(true)}
                    data-testid="button-add-manual-student"
                  >
                    <PlusCircle size={14} className="mr-1" />
                    Add Student
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search and Filter Controls */}
                <div className="mb-6 space-y-4">
                  {/* Search Input */}
                  <div>
                    <Label htmlFor="studentSearch">Search Students</Label>
                    <div className="relative">
                      <Search
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                        size={16}
                      />
                      <Input
                        id="studentSearch"
                        placeholder="Search by name, email, or class name..."
                        value={studentSearchTerm}
                        onChange={e => setStudentSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="input-student-search"
                      />
                    </div>
                  </div>

                  {/* Student Status Filter */}
                  <div>
                    <Label htmlFor="studentStatusFilter">Student Status</Label>
                    <Select value={studentStatusFilter} onValueChange={setStudentStatusFilter}>
                      <SelectTrigger data-testid="select-student-status-filter">
                        <SelectValue placeholder="Select student status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Students</SelectItem>
                        <SelectItem value="active">Active Students</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Session Filter Dropdown */}
                  <div>
                    <Label htmlFor="sessionFilter">Filter by Class Session</Label>
                    <Select value={selectedSessionFilter} onValueChange={setSelectedSessionFilter}>
                      <SelectTrigger data-testid="select-session-filter">
                        <SelectValue placeholder="Select a session to filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sessions</SelectItem>
                        {allUpcomingSessions.map((session: any) => (
                          <SelectItem key={session.id} value={session.id}>
                            {session.className} ·{' '}
                            {formatSessionCompact(session.startDateTime, session.endDateTime)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {filteredStudents &&
                Array.isArray(filteredStudents) &&
                filteredStudents.length > 0 ? (
                  <div className="space-y-4">
                    {filteredStudents.map((student: any, index: number) => (
                      <div
                        key={`${student.studentEmail}_${student.studentName}_${index}`}
                        className="border rounded-lg p-4"
                        data-testid={`student-${index}`}
                      >
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <h3
                              className="font-semibold break-words"
                              data-testid={`student-name-${index}`}
                            >
                              {student.studentName}
                            </h3>
                            <p
                              className="text-sm font-medium text-primary"
                              data-testid={`student-class-count-${index}`}
                            >
                              {student.isManualStudent && student.totalClasses === 0
                                ? 'Manual student'
                                : `${student.totalClasses} ${
                                    student.totalClasses === 1 ? 'class' : 'classes'
                                  } enrolled`}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                              <div className="flex items-center gap-1 min-w-0">
                                <Mail size={14} className="shrink-0" />
                                <span className="break-all" data-testid={`student-email-${index}`}>
                                  {student.studentEmail}
                                </span>
                              </div>
                              {student.studentPhone && (
                                <div className="flex items-center gap-1">
                                  <Phone size={14} className="shrink-0" />
                                  <span data-testid={`student-phone-${index}`}>
                                    {student.studentPhone}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 shrink-0">
                            <Badge
                              variant={student.hasActiveSessions ? 'default' : 'secondary'}
                              data-testid={`student-status-${index}`}
                            >
                              {student.hasActiveSessions ? 'Active' : 'Past'}
                            </Badge>
                            {student.totalAmount > 0 && (
                              <p
                                className="text-sm font-semibold text-green-600"
                                data-testid={`student-total-amount-${index}`}
                              >
                                ${student.totalAmount.toFixed(2)} total
                              </p>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                // Sort student enrollments by date (newest first)
                                const sortedEnrollments = [...student.enrollments].sort((a, b) => {
                                  const dateA = a.sessionStartDateTime
                                    ? new Date(a.sessionStartDateTime)
                                    : new Date(a.enrolledAt);
                                  const dateB = b.sessionStartDateTime
                                    ? new Date(b.sessionStartDateTime)
                                    : new Date(b.enrolledAt);
                                  return dateB.getTime() - dateA.getTime();
                                });
                                setViewingStudentHistory({
                                  studentName: student.studentName,
                                  studentEmail: student.studentEmail,
                                  enrollments: sortedEnrollments,
                                });
                              }}
                              data-testid={`button-view-classes-${index}`}
                            >
                              <BookOpen size={14} className="mr-1" />
                              Classes
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setViewingStudentWaivers(student);
                                setSelectedWaiverClassId('');
                              }}
                              data-testid={`button-view-waivers-${index}`}
                            >
                              <FileText size={14} className="mr-1" />
                              Waivers
                            </Button>
                            {student.latestEnrollmentDate && (
                              <p
                                className="text-sm text-muted-foreground"
                                data-testid={`student-latest-enrollment-${index}`}
                              >
                                Latest: {formatDateCompact(student.latestEnrollmentDate)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="no-students">
                    {(Array.isArray(enrollments) && enrollments.length > 0) ||
                    (Array.isArray(manualStudents) && manualStudents.length > 0) ? (
                      <div>
                        <p className="text-muted-foreground">
                          No students found matching your search criteria.
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Try adjusting your search term or session filter.
                        </p>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">
                        No students have enrolled in your classes yet.
                      </p>
                    )}
                  </div>
                )}

                <Dialog open={isAddingManualStudent} onOpenChange={setIsAddingManualStudent}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Add Student</DialogTitle>
                      <DialogDescription>
                        Add a walk-in or offline student so you can send waivers manually.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="manual-student-name">Name</Label>
                        <Input
                          id="manual-student-name"
                          value={manualStudentForm.name}
                          onChange={event =>
                            setManualStudentForm(current => ({
                              ...current,
                              name: event.target.value,
                            }))
                          }
                          placeholder="Student name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="manual-student-email">Email</Label>
                        <Input
                          id="manual-student-email"
                          type="email"
                          value={manualStudentForm.email}
                          onChange={event =>
                            setManualStudentForm(current => ({
                              ...current,
                              email: event.target.value,
                            }))
                          }
                          placeholder="student@example.com"
                        />
                      </div>
                      <div>
                        <Label htmlFor="manual-student-phone">Phone (optional)</Label>
                        <Input
                          id="manual-student-phone"
                          value={manualStudentForm.phoneNumber}
                          onChange={event =>
                            setManualStudentForm(current => ({
                              ...current,
                              phoneNumber: event.target.value,
                            }))
                          }
                          placeholder="Phone number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="manual-student-notes">Notes (optional)</Label>
                        <Textarea
                          id="manual-student-notes"
                          value={manualStudentForm.notes}
                          onChange={event =>
                            setManualStudentForm(current => ({
                              ...current,
                              notes: event.target.value,
                            }))
                          }
                          placeholder="Walk-in notes"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddingManualStudent(false)}>
                        Cancel
                      </Button>
                      <Button
                        disabled={saveManualStudentMutation.isPending}
                        onClick={() => saveManualStudentMutation.mutate(manualStudentForm)}
                      >
                        {saveManualStudentMutation.isPending ? 'Saving...' : 'Save Student'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog
                  open={!!viewingStudentWaivers}
                  onOpenChange={open => {
                    if (!open) {
                      setViewingStudentWaivers(null);
                      setSelectedWaiverClassId('');
                    }
                  }}
                >
                  <DialogContent className="max-w-2xl max-h-[80dvh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Waivers - {viewingStudentWaivers?.studentName}</DialogTitle>
                      <DialogDescription>{viewingStudentWaivers?.studentEmail}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5">
                      <div className="rounded-md border p-3">
                        <p className="font-medium mb-2">Send a class waiver</p>
                        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                          <Select
                            value={selectedWaiverClassId}
                            onValueChange={setSelectedWaiverClassId}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choose available waiver" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableWaivers.map((waiver: any) => (
                                <SelectItem key={waiver.id} value={waiver.id}>
                                  {waiver.title} - {waiver.waiverTitle || 'Liability Waiver'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            disabled={
                              !selectedWaiverClassId ||
                              !viewingStudentWaivers?.studentEmail ||
                              sendStudentWaiverMutation.isPending
                            }
                            onClick={() => {
                              if (!viewingStudentWaivers || !selectedWaiverClassId) return;
                              sendStudentWaiverMutation.mutate({
                                classId: selectedWaiverClassId,
                                recipientName: viewingStudentWaivers.studentName,
                                recipientEmail: viewingStudentWaivers.studentEmail,
                                recipientPhone: viewingStudentWaivers.studentPhone,
                              });
                            }}
                          >
                            <Mail size={14} className="mr-1" />
                            {sendStudentWaiverMutation.isPending ? 'Sending...' : 'Send'}
                          </Button>
                        </div>
                        {availableWaivers.length === 0 && (
                          <p className="mt-2 text-sm text-muted-foreground">
                            No active class waivers are available. Add waiver text to a class first.
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="font-medium mb-2">Waiver history</p>
                        {studentWaiversLoading ? (
                          <div className="flex justify-center py-6">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                          </div>
                        ) : studentWaivers.length > 0 ? (
                          <div className="space-y-2">
                            {studentWaivers.map((waiver: any) => (
                              <div key={waiver.id} className="rounded-md border px-3 py-2 text-sm">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <p className="font-medium">{waiver.classTitle}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {waiver.waiverTitle || 'Liability Waiver'} v
                                      {waiver.waiverVersion}
                                    </p>
                                  </div>
                                  <Badge
                                    variant={waiver.status === 'signed' ? 'default' : 'outline'}
                                  >
                                    {waiver.status === 'signed' ? 'Signed' : 'Pending'}
                                  </Badge>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                                  {waiver.sentAt && (
                                    <span>Sent {new Date(waiver.sentAt).toLocaleDateString()}</span>
                                  )}
                                  {waiver.signedAt && (
                                    <span>
                                      Signed {new Date(waiver.signedAt).toLocaleDateString()}
                                    </span>
                                  )}
                                  {waiver.status === 'signed' &&
                                    waiver.mediaReleaseConsent != null && (
                                      <span
                                        className={
                                          waiver.mediaReleaseConsent
                                            ? 'text-green-700'
                                            : 'text-amber-700'
                                        }
                                      >
                                        Photo release: {waiver.mediaReleaseConsent ? 'Yes' : 'No'}
                                      </span>
                                    )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                            No waivers have been sent or signed for this student email yet.
                          </p>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Student Class History Modal */}
                {viewingStudentHistory && (
                  <Dialog
                    open={!!viewingStudentHistory}
                    onOpenChange={() => setViewingStudentHistory(null)}
                  >
                    <DialogContent className="max-w-2xl max-h-[80dvh] overflow-hidden">
                      <DialogHeader>
                        <DialogTitle>
                          Class History - {viewingStudentHistory.studentName}
                        </DialogTitle>
                        <DialogDescription>{viewingStudentHistory.studentEmail}</DialogDescription>
                      </DialogHeader>
                      <div className="overflow-y-auto max-h-[60dvh] space-y-3">
                        {viewingStudentHistory.enrollments &&
                        viewingStudentHistory.enrollments.length > 0 ? (
                          viewingStudentHistory.enrollments.map(
                            (enrollment: any, index: number) => (
                              <div
                                key={`${enrollment.id}_${index}`}
                                className="border rounded-lg p-3"
                                data-testid={`history-enrollment-${index}`}
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h4
                                      className="font-medium"
                                      data-testid={`history-class-name-${index}`}
                                    >
                                      {enrollment.className}
                                    </h4>
                                    {enrollment.sessionStartDateTime ? (
                                      <p
                                        className="text-sm text-muted-foreground"
                                        data-testid={`history-session-date-${index}`}
                                      >
                                        {formatDateTimeRange(
                                          enrollment.sessionStartDateTime,
                                          enrollment.sessionEndDateTime
                                        )}
                                      </p>
                                    ) : (
                                      <p className="text-sm text-muted-foreground">
                                        No session scheduled
                                      </p>
                                    )}
                                    <div className="flex items-center gap-3 mt-2 text-sm">
                                      <span className="text-primary font-medium">
                                        {enrollment.ticketCount || 1}{' '}
                                        {(enrollment.ticketCount || 1) === 1 ? 'ticket' : 'tickets'}
                                      </span>
                                      {enrollment.totalAmount > 0 ? (
                                        <span className="text-green-600 font-medium">
                                          ${enrollment.totalAmount.toFixed(2)}
                                        </span>
                                      ) : (
                                        <span className="text-blue-600 font-medium">FREE</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <Badge
                                      variant={
                                        enrollment.sessionStartDateTime &&
                                        new Date(enrollment.sessionStartDateTime) > new Date()
                                          ? 'default'
                                          : 'secondary'
                                      }
                                      data-testid={`history-status-${index}`}
                                    >
                                      {enrollment.sessionStartDateTime &&
                                      new Date(enrollment.sessionStartDateTime) > new Date()
                                        ? 'Upcoming'
                                        : 'Past'}
                                    </Badge>
                                    <p
                                      className="text-sm text-muted-foreground mt-1"
                                      data-testid={`history-enrolled-date-${index}`}
                                    >
                                      Enrolled: {formatDateCompact(enrollment.enrolledAt)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-muted-foreground">No class history found.</p>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule">
            <Card data-testid="schedule-card">
              <CardHeader>
                <CardTitle>Class Schedule</CardTitle>
                <CardDescription>
                  View your upcoming class sessions and enrolled students
                </CardDescription>
              </CardHeader>
              <CardContent>
                {scheduleLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : schedule && Array.isArray(schedule) && schedule.length > 0 ? (
                  <div className="space-y-4">
                    {schedule.map((session: any) => {
                      const remainingSpots = getSessionRemainingSpots(session);
                      const full = isSessionFull(session);
                      return (
                        <div
                          key={session.sessionId}
                          className="border rounded-lg p-4"
                          data-testid={`schedule-session-${session.sessionId}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="font-medium text-primary">Class Name</p>
                                  <p
                                    className="text-foreground"
                                    data-testid={`schedule-class-name-${session.sessionId}`}
                                  >
                                    {session.classTitle}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-medium text-primary">Date</p>
                                  <p
                                    className="text-foreground"
                                    data-testid={`schedule-date-${session.sessionId}`}
                                  >
                                    {formatDateWithTimezone(session.startDateTime, {
                                      dateOnly: true,
                                    })}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-medium text-primary">Time</p>
                                  <p
                                    className="text-foreground"
                                    data-testid={`schedule-time-${session.sessionId}`}
                                  >
                                    {formatTimeRange(session.startDateTime, session.endDateTime)}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-medium text-primary">Students</p>
                                  <p
                                    className="text-foreground"
                                    data-testid={`schedule-students-${session.sessionId}`}
                                  >
                                    {session.enrolledCount} enrolled of{' '}
                                    {session.maxStudents ?? 'unlimited'} spots
                                    {remainingSpots !== null
                                      ? ` / ${remainingSpots} remaining`
                                      : ''}
                                  </p>
                                </div>
                              </div>
                              {session.notes && (
                                <div className="mt-3 pt-3 border-t">
                                  <p className="font-medium text-primary text-sm">Session Notes</p>
                                  <p
                                    className="text-muted-foreground text-sm"
                                    data-testid={`schedule-notes-${session.sessionId}`}
                                  >
                                    {session.notes}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="ml-4 flex flex-col gap-2 items-end">
                              <Badge
                                variant={full ? 'destructive' : 'outline'}
                                data-testid={`schedule-status-${session.sessionId}`}
                              >
                                {full ? 'Full' : 'Available'}
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setViewingRosterFor({
                                    sessionId: session.sessionId,
                                    classId: session.classId,
                                    sessionLabel: formatDateCompact(session.startDateTime),
                                  });
                                }}
                                data-testid={`button-view-enrollments-${session.sessionId}`}
                              >
                                <Users size={14} className="mr-1" />
                                Roster ({session.enrolledCount})
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="no-scheduled-sessions">
                    <p className="text-muted-foreground">No upcoming class sessions scheduled.</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Create classes and add sessions to see your schedule here.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="archive">
            <Card data-testid="archive-card">
              <CardHeader>
                <CardTitle>Class Archive</CardTitle>
                <CardDescription>
                  View your completed class sessions and enrolled students
                </CardDescription>
              </CardHeader>
              <CardContent>
                {archiveLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : archivedSessions &&
                  Array.isArray(archivedSessions) &&
                  archivedSessions.length > 0 ? (
                  <div className="space-y-4">
                    {archivedSessions.map((session: any) => (
                      <div
                        key={session.sessionId}
                        className="border rounded-lg p-4"
                        data-testid={`archive-session-${session.sessionId}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="font-medium text-primary">Class Name</p>
                                <p
                                  className="text-foreground"
                                  data-testid={`archive-class-name-${session.sessionId}`}
                                >
                                  {session.classTitle}
                                </p>
                              </div>
                              <div>
                                <p className="font-medium text-primary">Date</p>
                                <p
                                  className="text-foreground"
                                  data-testid={`archive-date-${session.sessionId}`}
                                >
                                  {formatDateWithTimezone(session.startDateTime, {
                                    dateOnly: true,
                                  })}
                                </p>
                              </div>
                              <div>
                                <p className="font-medium text-primary">Time</p>
                                <p
                                  className="text-foreground"
                                  data-testid={`archive-time-${session.sessionId}`}
                                >
                                  {formatTimeRange(session.startDateTime, session.endDateTime)}
                                </p>
                              </div>
                              <div>
                                <p className="font-medium text-primary">Students</p>
                                <p
                                  className="text-foreground"
                                  data-testid={`archive-students-${session.sessionId}`}
                                >
                                  {session.enrolledCount} enrolled of {session.maxStudents} spots
                                </p>
                              </div>
                            </div>
                            {session.notes && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="font-medium text-primary text-sm">Session Notes</p>
                                <p
                                  className="text-muted-foreground text-sm"
                                  data-testid={`archive-notes-${session.sessionId}`}
                                >
                                  {session.notes}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="ml-4 flex flex-col gap-2 items-end">
                            <Badge
                              variant="secondary"
                              data-testid={`archive-status-${session.sessionId}`}
                            >
                              Completed
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setViewingRosterFor({
                                  sessionId: session.sessionId,
                                  classId: session.classId,
                                  sessionLabel: formatDateCompact(session.startDateTime),
                                });
                              }}
                              data-testid={`button-view-archive-enrollments-${session.sessionId}`}
                            >
                              <Users size={14} className="mr-1" />
                              Roster ({session.enrolledCount})
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="no-archived-sessions">
                    <p className="text-muted-foreground">No completed class sessions yet.</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Completed sessions will appear here after they end.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="passes">
            <Card>
              <CardHeader>
                <CardTitle>Punch Passes</CardTitle>
                <CardDescription>
                  See who holds your passes. Restore or remove punches, extend expiry, or cancel a
                  booking and return its punch — every change is logged with your reason.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PassHoldersManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="refunds">
            <Card>
              <CardHeader>
                <CardTitle>Refund Requests</CardTitle>
                <CardDescription>
                  Review and respond to refund requests from your students.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {refundRequestsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : refundRequests && refundRequests.length > 0 ? (
                  <div className="space-y-4">
                    {refundRequests.map((req: any) => (
                      <div key={req.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-1">
                            <p className="font-semibold">{req.classTitle}</p>
                            <p className="text-sm text-muted-foreground">
                              {req.purchaserName} &middot; {req.purchaserEmail}
                            </p>
                            {req.sessionStartDateTime && (
                              <p className="text-sm text-muted-foreground">
                                Session:{' '}
                                {formatDateWithTimezone(req.sessionStartDateTime, {
                                  includeTime: true,
                                })}
                              </p>
                            )}
                            {req.createdAt && (
                              <p className="text-xs text-muted-foreground">
                                Requested{' '}
                                {formatDateWithTimezone(req.createdAt, { includeTime: true })}
                              </p>
                            )}
                            {req.requestedReason && (
                              <p className="text-sm italic text-muted-foreground">
                                &ldquo;{req.requestedReason}&rdquo;
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 text-right space-y-1">
                            {req.refundAmountCents != null && (
                              <p className="font-semibold">
                                ${(req.refundAmountCents / 100).toFixed(2)}
                              </p>
                            )}
                            <Badge
                              variant={
                                req.status === 'pending'
                                  ? 'outline'
                                  : req.status === 'approved'
                                    ? 'default'
                                    : 'secondary'
                              }
                            >
                              {req.status}
                            </Badge>
                          </div>
                        </div>
                        {(req.status === 'pending' || req.status === 'creator_review') &&
                          denyingRefundId !== req.id && (
                            <div className="space-y-1 pt-1">
                              {(req.orderNetAmountCents ?? 0) <= 0 ? (
                                <>
                                  <p className="text-[11px] font-medium text-muted-foreground">
                                    Free booking — no payment or processing fee.
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        reviewRefundMutation.mutate({
                                          refundRequestId: req.id,
                                          action: 'approve',
                                          feeBearer: 'creator',
                                        })
                                      }
                                      disabled={reviewRefundMutation.isPending}
                                      data-testid={`button-approve-refund-${req.id}`}
                                    >
                                      Approve cancellation
                                    </Button>
                                    {req.denyAllowed && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setDenyingRefundId(req.id);
                                          setDenyReason('');
                                        }}
                                        disabled={reviewRefundMutation.isPending}
                                        data-testid={`button-deny-refund-${req.id}`}
                                      >
                                        Deny
                                      </Button>
                                    )}
                                  </div>
                                  {!req.denyAllowed && (
                                    <p className="text-[11px] text-muted-foreground">
                                      This is within the policy window — it must be approved.
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <p className="text-[11px] font-medium text-muted-foreground">
                                    Approve — who pays the card processing fee?
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        reviewRefundMutation.mutate({
                                          refundRequestId: req.id,
                                          action: 'approve',
                                          feeBearer: 'customer',
                                        })
                                      }
                                      disabled={reviewRefundMutation.isPending}
                                      data-testid={`button-approve-refund-${req.id}`}
                                      title="Refund the amount minus the fee (the student pays it)"
                                    >
                                      Customer pays the fee
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={() =>
                                        reviewRefundMutation.mutate({
                                          refundRequestId: req.id,
                                          action: 'approve',
                                          feeBearer: 'creator',
                                        })
                                      }
                                      disabled={reviewRefundMutation.isPending}
                                      title="Full refund; you pay the processing fee"
                                    >
                                      I pay the fee (full refund)
                                    </Button>
                                    {req.denyAllowed && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setDenyingRefundId(req.id);
                                          setDenyReason('');
                                        }}
                                        disabled={reviewRefundMutation.isPending}
                                        data-testid={`button-deny-refund-${req.id}`}
                                      >
                                        Deny
                                      </Button>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-muted-foreground">
                                    “Customer pays the fee” refunds the amount minus the processing
                                    fee; “I pay the fee” is a full refund you absorb.
                                    {!req.denyAllowed &&
                                      ' This is within the policy window — it must be refunded.'}
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        {(req.status === 'pending' || req.status === 'creator_review') &&
                          denyingRefundId === req.id && (
                            <div className="space-y-2 pt-1">
                              <Input
                                placeholder="Reason for denial (required)"
                                value={denyReason}
                                onChange={e => setDenyReason(e.target.value)}
                                className="rounded-xl"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    if (!denyReason.trim()) return;
                                    reviewRefundMutation.mutate({
                                      refundRequestId: req.id,
                                      action: 'deny',
                                      denialReason: denyReason,
                                    });
                                    setDenyingRefundId(null);
                                  }}
                                  disabled={!denyReason.trim() || reviewRefundMutation.isPending}
                                >
                                  Confirm denial
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setDenyingRefundId(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        {req.status === 'denied' && req.denialReason && (
                          <p className="text-sm text-muted-foreground">
                            Denial reason: {req.denialReason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No refund requests.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="earnings">
            <div className="space-y-6">
              {/* Connect status card */}
              {connectStatus && (
                <Card
                  className={
                    connectStatus.chargesEnabled ? 'border-green-300' : 'border-orange-300'
                  }
                >
                  <CardContent className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <DollarSign
                        size={20}
                        className={
                          connectStatus.chargesEnabled ? 'text-green-600' : 'text-orange-600'
                        }
                      />
                      <div>
                        <p className="font-medium text-sm">
                          {connectStatus.chargesEnabled
                            ? 'Payouts active'
                            : connectStatus.onboardingStatus === 'in_progress'
                              ? 'Payout setup in progress'
                              : 'Payout account not connected'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {connectStatus.chargesEnabled
                            ? 'Your earnings will be transferred after the holding period.'
                            : 'Complete Stripe onboarding to receive your earnings.'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      {!connectStatus.chargesEnabled && (
                        <Button
                          size="sm"
                          onClick={() => connectOnboardMutation.mutate()}
                          disabled={connectOnboardMutation.isPending}
                        >
                          {connectOnboardMutation.isPending
                            ? 'Redirecting…'
                            : connectStatus.onboardingStatus === 'in_progress'
                              ? 'Continue setup'
                              : 'Set up payouts'}
                        </Button>
                      )}
                      {connectStatus.connected && (
                        <Button
                          size="sm"
                          variant={connectStatus.chargesEnabled ? 'default' : 'outline'}
                          onClick={() => connectDashboardMutation.mutate()}
                          disabled={connectDashboardMutation.isPending}
                        >
                          {connectDashboardMutation.isPending ? 'Opening…' : 'Edit Stripe settings'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Lifetime Gross</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {earningsSummaryLoading ? (
                      <div className="h-8 w-24 bg-muted animate-pulse rounded" />
                    ) : (
                      <p className="text-2xl font-bold">
                        $
                        {(
                          ((earningsSummary?.classSaleCents ?? 0) +
                            (earningsSummary?.tipCents ?? 0)) /
                          100
                        ).toFixed(2)}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Class sales + tips</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Net Earned</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {earningsSummaryLoading ? (
                      <div className="h-8 w-24 bg-muted animate-pulse rounded" />
                    ) : (
                      <p className="text-2xl font-bold">
                        ${((earningsSummary?.netEarnedCents ?? 0) / 100).toFixed(2)}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      After refunds &amp; chargebacks
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Pending Payout</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {earningsSummaryLoading ? (
                      <div className="h-8 w-24 bg-muted animate-pulse rounded" />
                    ) : (
                      <p className="text-2xl font-bold">
                        ${((earningsSummary?.pendingPayoutCents ?? 0) / 100).toFixed(2)}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Scheduled for transfer</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Total Refunded</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {earningsSummaryLoading ? (
                      <div className="h-8 w-24 bg-muted animate-pulse rounded" />
                    ) : (
                      <p className="text-2xl font-bold text-destructive">
                        $
                        {Math.abs(
                          ((earningsSummary?.refundCents ?? 0) +
                            (earningsSummary?.chargebackCents ?? 0)) /
                            100
                        ).toFixed(2)}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Refunds &amp; chargebacks</p>
                  </CardContent>
                </Card>
              </div>

              {/* Ledger history */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Transaction History</CardTitle>
                    <CardDescription>Most recent 25 ledger entries</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.location.href = '/api/creator/earnings/statement.csv';
                    }}
                  >
                    <Download size={14} className="mr-2" />
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  {earningsLedgerLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                    </div>
                  ) : earningsLedger && earningsLedger.length > 0 ? (
                    <div className="overflow-x-auto overscroll-x-contain">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-muted-foreground">
                            <th className="text-left py-2 pr-4 font-medium">Date</th>
                            <th className="text-left py-2 pr-4 font-medium">Type</th>
                            <th className="text-left py-2 pr-4 font-medium">Purchaser</th>
                            <th className="text-left py-2 pr-4 font-medium">Item</th>
                            <th className="text-right py-2 pr-4 font-medium">Qty</th>
                            <th className="text-left py-2 pr-4 font-medium">Reference</th>
                            <th className="text-right py-2 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {earningsLedger.map(entry => {
                            const amountDollars = entry.amountCents / 100;
                            const isNegative = amountDollars < 0;
                            return (
                              <tr key={entry.id} className="border-b last:border-0">
                                <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">
                                  {formatDateCompact(entry.createdAt)}
                                </td>
                                <td className="py-2 pr-4">
                                  <Badge variant="outline" className="capitalize text-xs">
                                    {entry.entryType.replace(/_/g, ' ')}
                                  </Badge>
                                </td>
                                <td className="py-2 pr-4 max-w-[180px]">
                                  {entry.purchaserName ? (
                                    <div className="min-w-0">
                                      <p className="truncate font-medium">{entry.purchaserName}</p>
                                      {entry.purchaserEmail && (
                                        <p className="truncate text-xs text-muted-foreground">
                                          {entry.purchaserEmail}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="py-2 pr-4 max-w-[200px]">
                                  {entry.itemTitle ? (
                                    <div className="min-w-0">
                                      <p className="truncate">{entry.itemTitle}</p>
                                      {entry.sessionStartDateTime && (
                                        <p className="truncate text-xs text-muted-foreground">
                                          {formatDateCompact(entry.sessionStartDateTime)}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">
                                  {entry.quantity ?? '—'}
                                </td>
                                <td className="py-2 pr-4 text-muted-foreground truncate max-w-[160px]">
                                  {entry.referenceNote ?? entry.orderId ?? '—'}
                                </td>
                                <td className="py-2 text-right">
                                  <p
                                    className={`font-medium tabular-nums ${isNegative ? 'text-destructive' : 'text-foreground'}`}
                                  >
                                    {isNegative ? '-' : '+'}${Math.abs(amountDollars).toFixed(2)}
                                  </p>
                                  {typeof entry.orderGrossAmountCents === 'number' &&
                                    entry.orderGrossAmountCents !== Math.abs(entry.amountCents) && (
                                      <p className="text-xs text-muted-foreground tabular-nums">
                                        of ${(entry.orderGrossAmountCents / 100).toFixed(2)} gross
                                      </p>
                                    )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">No transactions yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payout history */}
              <Card>
                <CardHeader>
                  <CardTitle>Payout Schedule</CardTitle>
                  <CardDescription>Scheduled and completed payout transfers</CardDescription>
                </CardHeader>
                <CardContent>
                  {payoutHistoryLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                    </div>
                  ) : payoutHistory && payoutHistory.length > 0 ? (
                    <div className="space-y-3">
                      {payoutHistory.map(payout => (
                        <div
                          key={payout.id}
                          className="flex items-center justify-between border rounded-lg p-3"
                        >
                          <div className="space-y-0.5">
                            <p className="text-sm font-medium">
                              {payout.eligibleAt
                                ? `Eligible ${formatDateCompact(payout.eligibleAt)}`
                                : 'Pending eligibility'}
                            </p>
                            {payout.holdReason && (
                              <p className="text-xs text-muted-foreground">
                                Hold: {payout.holdReason}
                              </p>
                            )}
                            {payout.stripeTransferId && (
                              <p className="text-xs text-muted-foreground font-mono">
                                {payout.stripeTransferId}
                              </p>
                            )}
                          </div>
                          <div className="text-right space-y-1">
                            {payout.creatorEarningsCents != null && (
                              <p className="font-semibold">
                                ${(payout.creatorEarningsCents / 100).toFixed(2)}
                              </p>
                            )}
                            <Badge
                              variant={
                                payout.status === 'completed'
                                  ? 'default'
                                  : payout.status === 'scheduled'
                                    ? 'outline'
                                    : 'secondary'
                              }
                              className="capitalize"
                            >
                              {payout.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">No payouts scheduled yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="broadcast">
            <Card>
              <CardHeader>
                <CardTitle>Broadcast Message</CardTitle>
                <CardDescription>
                  Email everyone who's taken this class, or just the students of a specific session.
                  Replies come back to you, and you receive a copy as confirmation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Class</Label>
                  <select
                    className="w-full mt-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    value={broadcastClassId}
                    onChange={e => {
                      setBroadcastClassId(e.target.value);
                      setBroadcastSessionId('');
                    }}
                    aria-label="Select class for broadcast"
                  >
                    <option value="">Select a class…</option>
                    {Array.isArray(classes) &&
                      classes.map((cls: any) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.title}
                        </option>
                      ))}
                  </select>
                </div>
                {broadcastClassId && (
                  <div>
                    <Label>Send to</Label>
                    <select
                      className="w-full mt-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      value={broadcastAudience}
                      onChange={e => setBroadcastAudience(e.target.value as 'all' | 'session')}
                      aria-label="Select broadcast audience"
                    >
                      <option value="all">Everyone who&apos;s taken this class</option>
                      <option value="session">A specific session</option>
                    </select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {broadcastAudience === 'all'
                        ? 'Reaches every student with an active enrollment in any session of this class (one email each).'
                        : 'Reaches only the students enrolled in the session you pick.'}
                    </p>
                  </div>
                )}
                {broadcastClassId && broadcastAudience === 'session' && (
                  <div>
                    <Label>Session</Label>
                    <select
                      className="w-full mt-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      value={broadcastSessionId}
                      onChange={e => setBroadcastSessionId(e.target.value)}
                      aria-label="Select session for broadcast"
                    >
                      <option value="">Select a session…</option>
                      {broadcastSessions.map(s => (
                        <option key={s.id} value={s.id}>
                          {formatSessionCompact(s.startDateTime, s.endDateTime)}
                          {s.isPast ? ' (past)' : ''} · {s.enrolledCount}{' '}
                          {s.enrolledCount === 1 ? 'student' : 'students'}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Shows all upcoming sessions plus the 2 most recent past ones.
                    </p>
                  </div>
                )}
                <div>
                  <Label>Subject</Label>
                  <Input
                    className="mt-1"
                    value={broadcastSubject}
                    onChange={e => setBroadcastSubject(e.target.value)}
                    placeholder="e.g. Exciting update about our class!"
                  />
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea
                    className="mt-1"
                    rows={6}
                    value={broadcastBody}
                    onChange={e => setBroadcastBody(e.target.value)}
                    placeholder="Write your message here…"
                  />
                </div>
                <Button
                  onClick={() => broadcastMutation.mutate()}
                  disabled={
                    !broadcastClassId ||
                    (broadcastAudience === 'session' && !broadcastSessionId) ||
                    broadcastSubject.trim().length < 3 ||
                    broadcastBody.trim().length < 10 ||
                    broadcastMutation.isPending
                  }
                >
                  <Mail size={16} className="mr-2" />
                  {broadcastMutation.isPending
                    ? 'Sending…'
                    : broadcastAudience === 'all'
                      ? 'Send to all students'
                      : 'Send to session'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {user?.isBlogWriter && (
            <TabsContent value="writing">
              <Card>
                <CardContent className="pt-6">
                  <WriterPortal />
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </PublicShell>
  );
}
